import { useState, useEffect, useMemo, useCallback, useRef, Fragment } from "react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Separator } from "@/components/ui/separator";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  TrendingUp,
  Lock,
  Link2,
  Save,
  Info,
  AlertCircle,
  AlertTriangle,
  ArrowUpRight,
  ArrowDownRight,
  ChevronsUpDown,
  ChevronDown,
  Check,
  Trash2,
  Copy,
  Shield,
  Gauge,
  Package,
  Ban,
  CircleOff,
  Plus,
  Pencil,
  Loader2,
  Layers,
  ChevronRight,
} from "lucide-react";
import { cn, formatEur } from "@/lib/utils";
import { TABLA_ANIOS, ColsAnios } from "@/components/ui/tabla-anios";
import {
  getProyeccionLineas,
  getProyeccionDrivers,
  saveProyeccionLinea,
  saveProyeccionLineasMasivo,
  crearCuenta,
  borrarCuenta,
  renombrarCuenta,
  renombrarCuentaEmpresa,
  saveProyeccionConfig,
} from "@/lib/api";
import { esCuentaDeAsiento } from "@/lib/useModoAsiento";

// ---------------------------------------------------------------------------
// Formatos
// ---------------------------------------------------------------------------

// Formato único de proyección: una sola fuente en lib/utils (formatEur).
const formatCurrency = formatEur;

const nfPct = new Intl.NumberFormat("es-ES", {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

// "12,4%" sin signo
const formatPctPlain = (value) => {
  if (value === null || value === undefined || isNaN(value)) return "—";
  return `${nfPct.format(value)}%`;
};

// "+5,2%" con signo
const formatPctSigned = (value) => {
  if (value === null || value === undefined || isNaN(value)) return "n/a";
  const sign = value >= 0 ? "+" : "";
  return `${sign}${nfPct.format(value)}%`;
};

// ---------------------------------------------------------------------------
// Métricas de crecimiento histórico (client-side, para el formulario Crecimiento)
// ---------------------------------------------------------------------------

const calcularCrecimientos = (valores) => {
  if (!valores || valores.length === 0) return null;

  const sortedValores = [...valores]
    .filter((v) => v.valor !== null && v.valor !== undefined)
    .sort((a, b) => a.anio - b.anio);
  const n = sortedValores.length;

  if (n < 2) {
    return {
      yoy1: null,
      cagr2: null,
      cagr3: null,
      cagr4: null,
      media: null,
      mejor: null,
      peor: null,
      ultimoAnio: sortedValores[n - 1]?.anio,
      ultimoValor: sortedValores[n - 1]?.valor,
    };
  }

  const yoyRates = [];
  for (let i = 1; i < n; i++) {
    const prev = sortedValores[i - 1].valor;
    const curr = sortedValores[i].valor;
    if (prev && prev !== 0) {
      yoyRates.push(((curr - prev) / Math.abs(prev)) * 100);
    }
  }

  const yoy1 = yoyRates.length > 0 ? yoyRates[yoyRates.length - 1] : null;

  let cagr2 = null;
  if (n >= 3) {
    const v0 = sortedValores[n - 3].valor;
    const v2 = sortedValores[n - 1].valor;
    if (v0 && v0 > 0 && v2 > 0) cagr2 = (Math.pow(v2 / v0, 1 / 2) - 1) * 100;
  }

  let cagr3 = null;
  if (n >= 4) {
    const v0 = sortedValores[n - 4].valor;
    const v3 = sortedValores[n - 1].valor;
    if (v0 && v0 > 0 && v3 > 0) cagr3 = (Math.pow(v3 / v0, 1 / 3) - 1) * 100;
  }

  let cagr4 = null;
  if (n >= 5) {
    const v0 = sortedValores[n - 5].valor;
    const v4 = sortedValores[n - 1].valor;
    if (v0 && v0 > 0 && v4 > 0) cagr4 = (Math.pow(v4 / v0, 1 / 4) - 1) * 100;
  }

  const media = yoyRates.length > 0 ? yoyRates.reduce((a, b) => a + b, 0) / yoyRates.length : null;
  const mejor = yoyRates.length > 0 ? Math.max(...yoyRates) : null;
  const peor = yoyRates.length > 0 ? Math.min(...yoyRates) : null;

  return {
    yoy1,
    cagr2,
    cagr3,
    cagr4,
    media,
    mejor,
    peor,
    ultimoAnio: sortedValores[n - 1]?.anio,
    ultimoValor: sortedValores[n - 1]?.valor,
  };
};

// Métodos de crecimiento agrupados: histórico (calculados) vs definidos por el usuario
const metodosHistoricos = [
  { id: "yoy1", label: "YoY1", descripcion: "Crecimiento último año" },
  { id: "cagr2", label: "CAGR2", descripcion: "CAGR 2 años" },
  { id: "cagr3", label: "CAGR3", descripcion: "CAGR 3 años" },
  { id: "cagr4", label: "CAGR4", descripcion: "CAGR 4 años" },
  { id: "media", label: "Media", descripcion: "Media de crecimientos" },
  { id: "mejor", label: "Mejor", descripcion: "Mejor año" },
  { id: "peor", label: "Peor", descripcion: "Peor año" },
  { id: "tendencia", label: "Tendencia", descripcion: "Regresión lineal del histórico" },
];

const metodosDefinidos = [
  { id: "manual", label: "Manual", descripcion: "Tasa manual" },
  { id: "por_anio", label: "Por año", descripcion: "Una tasa para cada año" },
  { id: "rampa", label: "Rampa", descripcion: "Tasa inicial + incremento anual" },
  { id: "ipc", label: "IPC", descripcion: "Año anterior × (1+IPC)" },
];

// Métodos con % calculable client-side (para los badges informativos)
const metodosCrecimiento = metodosHistoricos.filter((m) => m.id !== "tendencia");

// Métodos basados en tasa: admiten límites de seguridad (min/max %)
const METODOS_TASA = new Set([
  "yoy1",
  "cagr2",
  "cagr3",
  "cagr4",
  "media",
  "mejor",
  "peor",
  "manual",
  "por_anio",
  "rampa",
]);

// Opciones del selector de nivel. Desde el 2026-09-30 el selector vive EN
// CADA tabla (queja Pau: nivel por pestaña, recordado vía `storageKey`);
// se mantiene exportado por si algún uso externo lo necesita.
export const nivelesProyeccion = [
  { id: "1D", label: "1 Dígito" },
  { id: "3D", label: "3 Dígitos" },
  { id: "MAX", label: "Máximo detalle" },
];

const seccionesLabel = {
  ingresos: "Ingresos",
  otros_ingresos: "Otros ingresos",
  gastos: "Gastos",
};

// Prefijo de código esperado por sección de PyG. La proyección deriva sus
// líneas de las cuentas por prefijo de código, así que una cuenta nueva solo
// aparece en la sección si su código empieza por el prefijo correcto.
const SECCIONES_PYG = new Set(["ingresos", "otros_ingresos", "gastos"]);
const hintPrefijoSeccion = {
  ingresos: "empieza por 70…",
  otros_ingresos: "74/75…",
  gastos: "60/62/64…",
};

// Color del YoY: en gastos se invierte la semántica (subir gasto = malo)
const yoyColorClass = (pct, invertir) => {
  if (pct === null || pct === undefined || isNaN(pct)) return "text-muted-foreground";
  const positivoBueno = invertir ? pct < 0 : pct > 0;
  if (pct === 0) return "text-muted-foreground";
  return positivoBueno ? "text-emerald-600 dark:text-emerald-400" : "text-rose-600 dark:text-rose-400";
};

// ---------------------------------------------------------------------------
// Sparkline SVG inline (histórico sólido + proyección discontinua)
// ---------------------------------------------------------------------------

function Sparkline({ historico, proyeccion }) {
  const hist = (historico || []).filter((v) => v !== null && v !== undefined && !isNaN(v));
  const proy = (proyeccion || []).filter((v) => v !== null && v !== undefined && !isNaN(v));
  const all = [...hist, ...proy];
  if (all.length < 2) {
    return <span className="text-xs text-muted-foreground">—</span>;
  }

  const W = 100;
  const H = 28;
  const PAD = 3;
  const min = Math.min(...all);
  const max = Math.max(...all);
  const range = max - min || 1;
  const n = all.length;
  const x = (i) => PAD + (i * (W - 2 * PAD)) / (n - 1);
  const y = (v) => H - PAD - ((v - min) * (H - 2 * PAD)) / range;

  const histPoints = hist.map((v, i) => `${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(" ");
  // La proyección arranca en el último punto histórico para que la línea sea continua
  const proySeries = hist.length > 0 ? [hist[hist.length - 1], ...proy] : proy;
  const proyOffset = hist.length > 0 ? hist.length - 1 : 0;
  const proyPoints = proySeries
    .map((v, i) => `${x(proyOffset + i).toFixed(1)},${y(v).toFixed(1)}`)
    .join(" ");

  const unionX = hist.length > 0 ? x(hist.length - 1) : null;
  const unionY = hist.length > 0 ? y(hist[hist.length - 1]) : null;

  return (
    <svg
      width={W}
      height={H}
      viewBox={`0 0 ${W} ${H}`}
      className="inline-block align-middle"
      aria-hidden="true"
    >
      {hist.length >= 2 && (
        <polyline
          points={histPoints}
          fill="none"
          stroke="hsl(var(--muted-foreground))"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          opacity="0.7"
        />
      )}
      {proy.length > 0 && proySeries.length >= 2 && (
        <polyline
          points={proyPoints}
          fill="none"
          stroke="hsl(var(--primary))"
          strokeWidth="1.5"
          strokeDasharray="3 2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      )}
      {unionX !== null && proy.length > 0 && (
        <circle cx={unionX} cy={unionY} r="2.2" fill="hsl(var(--primary))" />
      )}
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Celda de año (importe + % s/ventas + YoY en proyectadas)
// ---------------------------------------------------------------------------

function CeldaAnio({ valor, pct, yoy, esProyectada, invertirSemantica, destacarYoY = false, esFallback = false }) {
  const mostrarYoY = esProyectada && yoy !== null && yoy !== undefined && !isNaN(yoy);
  const YoYIcon = mostrarYoY && yoy >= 0 ? ArrowUpRight : ArrowDownRight;
  return (
    <div className="text-right space-y-0.5">
      <div
        className={cn(
          "font-mono text-sm tabular-nums",
          esProyectada && "font-medium",
          // Fallback (sin driver): el modelo la proyecta constante/IPC; se
          // muestra atenuada para distinguirla de una proyección configurada.
          esFallback && "text-muted-foreground/70 font-normal"
        )}
        title={esFallback ? "Sin driver: el modelo proyecta esta línea automáticamente (constante o IPC)" : undefined}
      >
        {formatCurrency(valor)}
      </div>
      <div className="flex items-center justify-end gap-1.5 text-[11px] leading-none min-h-[12px]">
        {mostrarYoY && (
          <span
            className={cn(
              "inline-flex items-center gap-px font-medium",
              yoyColorClass(yoy, invertirSemantica),
              destacarYoY && "font-semibold"
            )}
          >
            <YoYIcon className="h-2.5 w-2.5" />
            {formatPctSigned(yoy)}
          </span>
        )}
        {pct !== null && pct !== undefined && !isNaN(pct) && (
          <span className={esProyectada ? "text-proy" : "text-muted-foreground"}>
            {formatPctPlain(pct)} s/ventas
          </span>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Badge de driver
// ---------------------------------------------------------------------------

function DriverBadge({ linea, onClick, readOnly }) {
  const clickable = !readOnly && onClick;
  const commonProps = clickable
    ? { onClick, role: "button", tabIndex: 0, onKeyDown: (e) => e.key === "Enter" && onClick() }
    : {};
  const cursorCls = clickable ? "cursor-pointer" : "cursor-default";

  if (linea.informativa && !linea.bloqueada_por) {
    // Fila informativa (estructura contable completa): sin driver posible.
    return <span className="text-xs text-muted-foreground">—</span>;
  }
  if (linea.bloqueada_por && linea.bloqueo_suave) {
    // Bloqueo declarado por el contenedor (p.ej. la 662 en Financiación):
    // solo el motivo en texto plano, como la maqueta.
    return (
      <span
        className="text-[11px] text-muted-foreground leading-tight inline-block max-w-[150px]"
        title={linea.bloqueada_por}
      >
        {linea.bloqueada_por}
      </span>
    );
  }
  if (linea.bloqueada_por) {
    return (
      <div className="flex flex-col items-center gap-0.5">
        <Badge
          variant="outline"
          className="gap-1 border-primary/50 text-primary text-[11px] font-medium"
        >
          <AlertTriangle className="h-3 w-3" />
          Bloqueada
        </Badge>
        <span className="text-[11px] text-muted-foreground max-w-[140px] truncate" title={`Bloqueada: ${linea.bloqueada_por}`}>
          Bloqueada: {linea.bloqueada_por}
        </span>
      </div>
    );
  }

  const driver = linea.driver;

  // El nivel de la vista es solo informativo: si el driver que manda vive en
  // OTRO nivel (p.ej. driver a 3 dígitos y vista por subcuenta, o al revés),
  // la línea no está "sin proyección" — muestra la proyección efectiva del
  // motor y este badge dice de dónde sale.
  const origen = linea.origen_proyeccion;
  if (origen && (!driver || linea.driver_ignorado)) {
    const primera = origen.drivers?.[0];
    const nivelLabel = { "1D": "1 dígito", "3D": "3 dígitos", MAX: "subcuenta" };
    let etiqueta;
    if (origen.tipo === "heredado") {
      etiqueta = `Desde ${primera?.codigo ?? "otro nivel"}`;
    } else if (origen.tipo === "desglose") {
      etiqueta = `Σ ${origen.n_drivers} con driver`;
    } else {
      etiqueta = `Drivers en ${origen.n_drivers} líneas`;
    }
    const detalleDrivers = (origen.drivers || [])
      .map((d) => `${d.codigo} (${nivelLabel[d.nivel] || d.nivel}): ${d.descripcion}`)
      .join("\n");
    const title =
      (origen.tipo === "heredado"
        ? "Proyectada por un driver de un nivel más agregado que cubre estas cuentas.\n"
        : origen.tipo === "desglose"
        ? "Proyectada por drivers configurados en sus subcuentas.\n"
        : "Proyectada por drivers de varios niveles.\n") +
      detalleDrivers +
      (origen.parcial_fallback ? "\nParte de las cuentas sin driver: automático (constante/IPC)." : "") +
      "\nEl nivel de la vista es solo informativo: el motor aplica siempre el driver más específico que cubre cada cuenta.";
    return (
      <div className="flex flex-col items-center gap-0.5">
        <Badge
          variant="outline"
          className={cn(
            "gap-1 bg-violet-100 text-violet-700 border-violet-300 hover:bg-violet-200 dark:bg-violet-950 dark:text-violet-300 dark:border-violet-800 dark:hover:bg-violet-900 text-[11px] font-medium max-w-[160px]",
            cursorCls
          )}
          title={title}
          {...commonProps}
        >
          <Layers className="h-3 w-3 shrink-0" />
          <span className="truncate">{etiqueta}</span>
        </Badge>
        {origen.tipo === "heredado" && primera?.descripcion && (
          <span className="text-[11px] text-muted-foreground max-w-[150px] truncate" title={primera.descripcion}>
            {primera.descripcion}
          </span>
        )}
      </div>
    );
  }

  if (!driver) {
    // Sin driver la línea NO desaparece del modelo: se proyecta en automático
    // (constante al último real, o al IPC global). Se dice explícitamente.
    const hintAuto = linea.es_fallback ? (
      <span className="text-[11px] text-muted-foreground">Auto: constante</span>
    ) : null;
    if (readOnly) {
      return (
        <div className="flex flex-col items-center gap-0.5">
          <span className="text-xs text-muted-foreground">—</span>
          {hintAuto}
        </div>
      );
    }
    return (
      <div className="flex flex-col items-center gap-0.5">
        <Button variant="ghost" size="sm" className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground" onClick={onClick}>
          Configurar
        </Button>
        {hintAuto}
      </div>
    );
  }

  if (driver.tipo === "crecimiento") {
    return (
      <Badge
        className={cn(
          "gap-1 bg-primary/15 text-primary border border-primary/30 hover:bg-primary/25 text-[11px] font-medium max-w-[160px]",
          cursorCls
        )}
        {...commonProps}
      >
        <TrendingUp className="h-3 w-3 shrink-0" />
        <span className="truncate">{driver.descripcion || "Crecimiento"}</span>
      </Badge>
    );
  }

  if (driver.tipo === "valor_fijo") {
    return (
      <Badge
        className={cn(
          "gap-1 bg-indigo-100 text-indigo-700 border border-indigo-300 hover:bg-indigo-200 dark:bg-indigo-950 dark:text-indigo-300 dark:border-indigo-800 dark:hover:bg-indigo-900 text-[11px] font-medium",
          cursorCls
        )}
        {...commonProps}
      >
        <Lock className="h-3 w-3 shrink-0" />
        {driver.params?.modo === "ipc"
          ? `Fijo + IPC (${driver.params.ipc_pct > 0 ? "+" : ""}${driver.params.ipc_pct}%)`
          : "Fijo"}
      </Badge>
    );
  }

  if (driver.tipo === "porcentaje_de") {
    return (
      <Badge
        className={cn(
          "gap-1 bg-sky-100 text-sky-700 border border-sky-300 hover:bg-sky-200 dark:bg-sky-950 dark:text-sky-300 dark:border-sky-800 dark:hover:bg-sky-900 text-[11px] font-medium max-w-[160px]",
          cursorCls
        )}
        {...commonProps}
      >
        <Link2 className="h-3 w-3 shrink-0" />
        <span className="truncate">{driver.descripcion || "% de otra línea"}</span>
      </Badge>
    );
  }

  if (driver.tipo === "elasticidad") {
    return (
      <Badge
        className={cn(
          "gap-1 bg-violet-100 text-violet-700 border border-violet-300 hover:bg-violet-200 dark:bg-violet-950 dark:text-violet-300 dark:border-violet-800 dark:hover:bg-violet-900 text-[11px] font-medium max-w-[160px]",
          cursorCls
        )}
        {...commonProps}
      >
        <Gauge className="h-3 w-3 shrink-0" />
        <span className="truncate">{driver.descripcion || "Elasticidad"}</span>
      </Badge>
    );
  }

  if (driver.tipo === "pxq") {
    return (
      <Badge
        className={cn(
          "gap-1 bg-emerald-100 text-emerald-700 border border-emerald-300 hover:bg-emerald-200 dark:bg-emerald-950 dark:text-emerald-300 dark:border-emerald-800 dark:hover:bg-emerald-900 text-[11px] font-medium max-w-[160px]",
          cursorCls
        )}
        {...commonProps}
      >
        <Package className="h-3 w-3 shrink-0" />
        <span className="truncate">{driver.descripcion || "Volumen × Precio"}</span>
      </Badge>
    );
  }

  return <span className="text-xs text-muted-foreground">—</span>;
}

// ---------------------------------------------------------------------------
// Tarjeta radio de tipo de driver
// ---------------------------------------------------------------------------

const tiposDriver = [
  { id: "crecimiento", titulo: "Crecimiento %", frase: "Crece sobre su propio histórico", icon: TrendingUp },
  { id: "valor_fijo", titulo: "Valor fijo", frase: "Importes concretos por año", icon: Lock },
  { id: "porcentaje_de", titulo: "% de otra línea", frase: "Depende de otra cuenta o partida", icon: Link2 },
  { id: "elasticidad", titulo: "Elasticidad", frase: "Crece α veces lo que crezca otra línea", icon: Gauge },
  { id: "pxq", titulo: "Volumen × Precio", frase: "Crecimiento de unidades y de precio", icon: Package },
  { id: "desactivado", titulo: "Desactivar", frase: "No proyectar: plana al último real", icon: Ban },
  // Punto 4 de Héctor (3-oct-2026): decisión EXPLÍCITA por línea de llevarla
  // a 0 en los años proyectados (la regla general sigue siendo "constante").
  { id: "a_cero", titulo: "A cero", frase: "0 € en todos los años proyectados", icon: CircleOff },
];

function TipoDriverCards({ value, onChange, disabled, tiposPermitidos = null }) {
  // tiposPermitidos: lista opcional de ids para restringir los tipos ofrecidos
  // (p.ej. ["valor_fijo"] para la amortización editable desde CAPEX). Sin él se
  // muestran todos (comportamiento de siempre).
  const tipos = tiposPermitidos
    ? tiposDriver.filter((t) => tiposPermitidos.includes(t.id))
    : tiposDriver;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" role="radiogroup">
      {tipos.map((t) => {
        const Icon = t.icon;
        const selected = value === t.id;
        return (
          <button
            key={t.id}
            type="button"
            role="radio"
            aria-checked={selected}
            disabled={disabled}
            onClick={() => onChange(t.id)}
            className={cn(
              "flex flex-col items-start gap-0.5 rounded-lg border p-2.5 text-left transition-colors",
              selected
                ? "border-primary ring-1 ring-primary bg-primary/5"
                : "border-border hover:bg-muted/40",
              disabled && "opacity-60 cursor-not-allowed"
            )}
          >
            <Icon className={cn("h-4 w-4", selected ? "text-primary" : "text-muted-foreground")} />
            <span className="text-sm font-medium leading-tight">{t.titulo}</span>
            <span className="text-[11px] text-muted-foreground leading-tight">{t.frase}</span>
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Dialog de configuración de driver (v2)
// ---------------------------------------------------------------------------

export function LineConfigDialog({
  open,
  onOpenChange,
  linea,
  aniosProyectados,
  onSave,
  baseCache, // { ingresos: [...], otros_ingresos: [...], gastos: [...] } | null
  baseCacheLoading,
  nivel,
  readOnly = false,
  tiposPermitidos = null, // restringe los tipos de driver ofrecidos (p.ej. ["valor_fijo"])
  ocultarValorFijoIpc = false, // F3: oculta el modo "Valor base + IPC" (amortización)
  // F2A: modo masivo. `linea` es un objeto sintético (sin histórico) y el driver
  // construido se aplica a varias líneas a la vez. Oculta el preview (carece de
  // sentido con líneas heterogéneas) y delega el toast de resultado en `onSave`.
  masivo = false,
  masivoCount = 0,
  // UX drivers (sept-2026): drivers guardados de TODOS los niveles de la
  // empresa, para avisar de solapes ANTES de guardar; onAnularDrivers(ids)
  // borra los que el usuario decida anular en el diálogo de conflicto.
  todosDrivers = [],
  onAnularDrivers = null,
}) {
  const tipoPorDefecto =
    tiposPermitidos && !tiposPermitidos.includes("crecimiento")
      ? tiposPermitidos[0]
      : "crecimiento";
  const [tipo, setTipo] = useState("crecimiento");
  // crecimiento
  const [metodo, setMetodo] = useState("yoy1");
  const [tasaManual, setTasaManual] = useState("0");
  const [tasasPorAnio, setTasasPorAnio] = useState({}); // metodo "por_anio"
  const [rampaInicial, setRampaInicial] = useState(""); // metodo "rampa"
  const [rampaIncremento, setRampaIncremento] = useState("");
  const [ipcPct, setIpcPct] = useState("2"); // metodo "ipc"
  const [ipcBase, setIpcBase] = useState("ultimo"); // F2B: "ultimo" | "media"
  // límites de seguridad (métodos de tasa)
  const [limitesOpen, setLimitesOpen] = useState(false);
  const [limiteMin, setLimiteMin] = useState("");
  const [limiteMax, setLimiteMax] = useState("");
  // valor_fijo
  const [valoresFijos, setValoresFijos] = useState({});
  const [valorFijoModo, setValorFijoModo] = useState("fijo"); // "fijo" | "ipc"
  const [valorFijoBase, setValorFijoBase] = useState(""); // base para modo ipc
  // porcentaje_de / elasticidad (comparten selector de base)
  const [baseTipo, setBaseTipo] = useState("ventas"); // "ventas" | "linea"
  const [baseLinea, setBaseLinea] = useState(null); // {seccion, codigo, nombre}
  const [basePickerOpen, setBasePickerOpen] = useState(false);
  const [modoPct, setModoPct] = useState("fijo"); // "fijo" | "historico" | "por_anio"
  // UX drivers: driver construido a la espera de que el usuario resuelva el
  // conflicto de solape (null = sin conflicto pendiente).
  const [conflicto, setConflicto] = useState(null);
  const [pctFijo, setPctFijo] = useState("0");
  const [pctsPorAnio, setPctsPorAnio] = useState({});
  // elasticidad
  const [alpha, setAlpha] = useState("1");
  // pxq
  const [pxqUnidades, setPxqUnidades] = useState("0");
  const [pxqPrecio, setPxqPrecio] = useState("0");

  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const crecimientos = useMemo(
    () => (linea ? calcularCrecimientos(linea.valores_historicos) : null),
    [linea]
  );

  // Inicializar estado desde el driver existente al abrir
  useEffect(() => {
    if (!open || !linea) return;
    const d = linea.driver;
    // UX drivers: nunca arrastrar una decisión de conflicto de una apertura
    // anterior — cada apertura parte limpia.
    setConflicto(null);

    // Reset de campos comunes nuevos (se sobreescriben abajo si el driver los trae)
    setTasasPorAnio({});
    setRampaInicial("");
    setRampaIncremento("");
    setIpcPct(d?.params?.ipc_pct != null ? String(d.params.ipc_pct) : "2");
    setIpcBase(d?.params?.base_ipc || "ultimo");
    setLimiteMin(d?.params?.limites?.min_pct != null ? String(d.params.limites.min_pct) : "");
    setLimiteMax(d?.params?.limites?.max_pct != null ? String(d.params.limites.max_pct) : "");
    setLimitesOpen(d?.params?.limites?.min_pct != null || d?.params?.limites?.max_pct != null);
    setAlpha("1");
    setPxqUnidades("0");
    setPxqPrecio("0");

    if (d?.tipo === "crecimiento") {
      setTipo("crecimiento");
      setMetodo(d.params?.metodo || "yoy1");
      setTasaManual(d.params?.tasa_manual !== null && d.params?.tasa_manual !== undefined ? String(d.params.tasa_manual) : "0");
      const tasas = {};
      aniosProyectados.forEach((a) => {
        const v = d.params?.tasas_por_anio?.[String(a)] ?? d.params?.tasas_por_anio?.[a];
        tasas[a] = v !== undefined && v !== null ? String(v) : "";
      });
      setTasasPorAnio(tasas);
      setRampaInicial(d.params?.rampa?.tasa_inicial != null ? String(d.params.rampa.tasa_inicial) : "");
      setRampaIncremento(d.params?.rampa?.incremento_anual != null ? String(d.params.rampa.incremento_anual) : "");
    } else if (d?.tipo === "elasticidad") {
      setTipo("elasticidad");
      const base = d.params?.base;
      if (base?.agregado === "VENTAS") {
        setBaseTipo("ventas");
        setBaseLinea(null);
      } else if (base?.codigo) {
        setBaseTipo("linea");
        setBaseLinea({ seccion: base.seccion, codigo: base.codigo, nombre: base.codigo });
      }
      setAlpha(d.params?.alpha != null ? String(d.params.alpha) : "1");
    } else if (d?.tipo === "pxq") {
      setTipo("pxq");
      setPxqUnidades(d.params?.crecimiento_unidades_pct != null ? String(d.params.crecimiento_unidades_pct) : "0");
      setPxqPrecio(d.params?.crecimiento_precio_pct != null ? String(d.params.crecimiento_precio_pct) : "0");
    } else if (d?.tipo === "valor_fijo") {
      setTipo("valor_fijo");
      if (d.params?.modo === "ipc") {
        setValorFijoModo("ipc");
        setValorFijoBase(d.params?.valor_base != null ? String(d.params.valor_base) : "");
        setIpcPct(d.params?.ipc_pct != null ? String(d.params.ipc_pct) : "2");
      } else {
        setValorFijoModo("fijo");
      }
      const vals = {};
      aniosProyectados.forEach((a) => {
        const v = d.params?.valores?.[String(a)] ?? d.params?.valores?.[a];
        vals[a] = v !== undefined && v !== null ? String(v) : "";
      });
      setValoresFijos(vals);
    } else if (d?.tipo === "porcentaje_de") {
      setTipo("porcentaje_de");
      const base = d.params?.base;
      if (base?.agregado === "VENTAS") {
        setBaseTipo("ventas");
        setBaseLinea(null);
      } else if (base?.codigo) {
        setBaseTipo("linea");
        setBaseLinea({ seccion: base.seccion, codigo: base.codigo, nombre: base.codigo });
      }
      setModoPct(d.params?.modo || "fijo");
      setPctFijo(d.params?.pct !== null && d.params?.pct !== undefined ? String(d.params.pct) : "0");
      const pcts = {};
      aniosProyectados.forEach((a) => {
        const v = d.params?.pcts_por_anio?.[String(a)] ?? d.params?.pcts_por_anio?.[a];
        pcts[a] = v !== undefined && v !== null ? String(v) : "";
      });
      setPctsPorAnio(pcts);
    } else if (d?.tipo === "desactivado") {
      setTipo("desactivado");
    } else if (d?.tipo === "a_cero") {
      setTipo("a_cero");
    } else {
      // Sin driver PROPIO: NINGÚN tipo preseleccionado (Héctor 3-oct, punto 3:
      // que no parezca que ya hay una proyección elegida — la que se ve es la
      // heredada/automática). Con tipos restringidos (p.ej. amortización solo
      // valor_fijo) sí se preselecciona el único posible.
      setTipo(tiposPermitidos ? tipoPorDefecto : null);
      setMetodo("yoy1");
      setTasaManual("0");
      setValoresFijos({});
      setConflicto(null);
      setValorFijoModo("fijo");
      setValorFijoBase("");
      setBaseTipo("ventas");
      setBaseLinea(null);
      setModoPct("fijo");
      setPctFijo("0");
      setPctsPorAnio({});
    }
  }, [open, linea, aniosProyectados]);

  // Resolver nombre legible de la línea base desde la caché
  useEffect(() => {
    if (!baseCache || !baseLinea || baseLinea.nombre !== baseLinea.codigo) return;
    const found = (baseCache[baseLinea.seccion] || []).find((l) => l.codigo === baseLinea.codigo);
    if (found) setBaseLinea((prev) => ({ ...prev, nombre: found.nombre }));
  }, [baseCache, baseLinea]);

  // Ventas proyectadas/históricas estimadas: suma de las líneas de ingresos cacheadas
  const ventasPorAnio = useMemo(() => {
    if (!baseCache?.ingresos) return {};
    const map = {};
    baseCache.ingresos.forEach((l) => {
      (l.proyeccion || []).forEach(({ anio, valor }) => {
        if (valor !== null && valor !== undefined) {
          map[anio] = (map[anio] || 0) + valor;
        }
      });
    });
    return map;
  }, [baseCache]);

  // Ventas históricas (para calcular el crecimiento de la base en elasticidad)
  const ventasHistPorAnio = useMemo(() => {
    if (!baseCache?.ingresos) return {};
    const map = {};
    baseCache.ingresos.forEach((l) => {
      (l.valores_historicos || []).forEach(({ anio, valor }) => {
        if (valor !== null && valor !== undefined) {
          map[anio] = (map[anio] || 0) + valor;
        }
      });
    });
    return map;
  }, [baseCache]);

  // Nº de años históricos con dato de la propia línea (requisito de "tendencia")
  const nAniosHist = useMemo(
    () =>
      (linea?.valores_historicos || []).filter(
        (v) => v.valor !== null && v.valor !== undefined
      ).length,
    [linea]
  );

  // Línea base seleccionada (objeto completo de la caché)
  const baseLineaObj = useMemo(() => {
    if (baseTipo !== "linea" || !baseLinea || !baseCache) return null;
    return (baseCache[baseLinea.seccion] || []).find((l) => l.codigo === baseLinea.codigo) || null;
  }, [baseTipo, baseLinea, baseCache]);

  // Tasa seleccionada para crecimiento (métodos de tasa única)
  const tasaSeleccionada = useMemo(() => {
    if (metodo === "manual") return parseFloat(tasaManual) || 0;
    if (metodo === "por_anio" || metodo === "rampa" || metodo === "tendencia") return null;
    return crecimientos?.[metodo] ?? null;
  }, [metodo, tasaManual, crecimientos]);

  // Aplica los límites de seguridad (min/max %) a una tasa
  const aplicarLimites = useCallback(
    (tasa) => {
      if (tasa === null || tasa === undefined || isNaN(tasa)) return tasa;
      let t = tasa;
      const mn = parseFloat(String(limiteMin).replace(",", "."));
      const mx = parseFloat(String(limiteMax).replace(",", "."));
      if (!isNaN(mn)) t = Math.max(t, mn);
      if (!isNaN(mx)) t = Math.min(t, mx);
      return t;
    },
    [limiteMin, limiteMax]
  );

  // Tasas de la rampa por año proyectado (para la frase de preview)
  const tasasRampa = useMemo(() => {
    const ini = parseFloat(String(rampaInicial).replace(",", "."));
    const inc = parseFloat(String(rampaIncremento).replace(",", ".")) || 0;
    if (isNaN(ini)) return null;
    return aniosProyectados.map((anio, i) => ({ anio, tasa: aplicarLimites(ini + inc * i) }));
  }, [rampaInicial, rampaIncremento, aniosProyectados, aplicarLimites]);

  // ---- PREVIEW ----
  const preview = useMemo(() => {
    if (!linea) return { filas: [], aviso: null };
    const filas = [];
    let aviso = null;

    if (tipo === "crecimiento") {
      if (metodo === "tendencia") {
        return {
          filas: [],
          aviso:
            nAniosHist < 3
              ? "La tendencia necesita al menos 3 años históricos"
              : "La tendencia la calcula el servidor: guarda el driver y verás la proyección en la tabla",
        };
      }
      if (crecimientos?.ultimoValor === null || crecimientos?.ultimoValor === undefined) {
        return { filas: [], aviso: "No hay datos históricos suficientes para calcular la proyección" };
      }
      // Tasa por año según método
      let tasaHeredada = 0;
      const tasaDelAnio = (anio, i) => {
        if (metodo === "por_anio") {
          const raw = tasasPorAnio[anio];
          const p = raw === "" || raw === undefined ? NaN : parseFloat(String(raw).replace(",", "."));
          if (!isNaN(p)) tasaHeredada = p;
          return tasaHeredada;
        }
        if (metodo === "rampa") {
          const ini = parseFloat(String(rampaInicial).replace(",", "."));
          const inc = parseFloat(String(rampaIncremento).replace(",", ".")) || 0;
          if (isNaN(ini)) return null;
          return ini + inc * i;
        }
        if (metodo === "ipc") {
          const p = parseFloat(String(ipcPct).replace(",", "."));
          return isNaN(p) ? null : p;
        }
        return tasaSeleccionada;
      };
      if (metodo === "rampa" && isNaN(parseFloat(String(rampaInicial).replace(",", ".")))) {
        return { filas: [], aviso: "Indica la tasa inicial de la rampa" };
      }
      let valor = crecimientos.ultimoValor;
      // F2B: IPC sobre media → arranca de la media del histórico
      if (metodo === "ipc" && ipcBase === "media") {
        const hv = (linea.valores_historicos || [])
          .map((v) => v.valor)
          .filter((v) => v !== null && v !== undefined);
        if (hv.length) valor = hv.reduce((s, v) => s + v, 0) / hv.length;
      }
      aniosProyectados.forEach((anio, i) => {
        const tasa = aplicarLimites(tasaDelAnio(anio, i));
        if (tasa === null || tasa === undefined || isNaN(tasa)) {
          filas.push({ anio, valor: null });
          return;
        }
        valor = valor * (1 + tasa / 100);
        filas.push({ anio, valor });
      });
    } else if (tipo === "elasticidad") {
      if (crecimientos?.ultimoValor === null || crecimientos?.ultimoValor === undefined) {
        return { filas: [], aviso: "No hay datos históricos suficientes para calcular la proyección" };
      }
      const a = parseFloat(String(alpha).replace(",", "."));
      if (isNaN(a)) {
        return { filas: [], aviso: "Indica el valor de α" };
      }
      // Serie de la base: último histórico + proyección
      const baseSerie = {};
      let ultimoHistBase = null;
      if (baseTipo === "ventas") {
        const aniosHistVentas = Object.keys(ventasHistPorAnio).map(Number).sort((x, y) => x - y);
        const ultimoAnioHistVentas = aniosHistVentas[aniosHistVentas.length - 1];
        ultimoHistBase = ultimoAnioHistVentas != null ? ventasHistPorAnio[ultimoAnioHistVentas] : null;
        aniosProyectados.forEach((anio) => {
          baseSerie[anio] = ventasPorAnio[anio] ?? null;
        });
      } else if (baseLineaObj) {
        const histSorted = [...(baseLineaObj.valores_historicos || [])]
          .filter((v) => v.valor !== null && v.valor !== undefined)
          .sort((x, y) => x.anio - y.anio);
        ultimoHistBase = histSorted[histSorted.length - 1]?.valor ?? null;
        const proyMap = {};
        (baseLineaObj.proyeccion || []).forEach(({ anio, valor }) => {
          proyMap[anio] = valor;
        });
        aniosProyectados.forEach((anio) => {
          baseSerie[anio] = proyMap[anio] ?? null;
        });
      } else if (baseTipo === "linea") {
        aviso = baseLinea ? "La base aún no tiene proyección" : "Selecciona una línea base";
      }

      const baseIncompleta =
        ultimoHistBase === null ||
        ultimoHistBase === undefined ||
        ultimoHistBase === 0 ||
        aniosProyectados.some((an) => baseSerie[an] === null || baseSerie[an] === undefined);
      if (!aviso && baseIncompleta) {
        aviso = "La base aún no tiene proyección";
      }

      let valor = crecimientos.ultimoValor;
      let prevBase = ultimoHistBase;
      aniosProyectados.forEach((anio) => {
        const b = baseSerie[anio];
        if (b === null || b === undefined || prevBase === null || prevBase === undefined || prevBase === 0) {
          filas.push({ anio, valor: null, base: b ?? null });
          prevBase = b ?? prevBase;
          return;
        }
        const crecBase = ((b - prevBase) / Math.abs(prevBase)) * 100;
        valor = valor * (1 + (a * crecBase) / 100);
        filas.push({ anio, valor, base: b });
        prevBase = b;
      });
    } else if (tipo === "pxq") {
      if (crecimientos?.ultimoValor === null || crecimientos?.ultimoValor === undefined) {
        return { filas: [], aviso: "No hay datos históricos suficientes para calcular la proyección" };
      }
      const u = parseFloat(String(pxqUnidades).replace(",", ".")) || 0;
      const p = parseFloat(String(pxqPrecio).replace(",", ".")) || 0;
      const tasaCombinada = (1 + u / 100) * (1 + p / 100) - 1;
      let valor = crecimientos.ultimoValor;
      aniosProyectados.forEach((anio) => {
        valor = valor * (1 + tasaCombinada);
        filas.push({ anio, valor });
      });
    } else if (tipo === "desactivado") {
      // F2C: plana al último real (0 si no hay histórico, como el backend)
      const v0 = crecimientos?.ultimoValor ?? 0;
      aniosProyectados.forEach((anio) => filas.push({ anio, valor: v0 }));
    } else if (tipo === "a_cero") {
      aniosProyectados.forEach((anio) => filas.push({ anio, valor: 0 }));
    } else if (tipo === "valor_fijo") {
      if (valorFijoModo === "ipc" && !ocultarValorFijoIpc) {
        const base = parseFloat(String(valorFijoBase).replace(",", "."));
        const ipc = (parseFloat(String(ipcPct).replace(",", ".")) || 0) / 100;
        aniosProyectados.forEach((anio, k) => {
          const v = isNaN(base) ? null : base * Math.pow(1 + ipc, k);
          filas.push({ anio, valor: v });
        });
      } else {
        aniosProyectados.forEach((anio) => {
          const raw = valoresFijos[anio];
          const v = raw === "" || raw === undefined ? null : parseFloat(raw);
          filas.push({ anio, valor: v !== null && !isNaN(v) ? v : null });
        });
      }
    } else if (tipo === "porcentaje_de") {
      // valores base por año
      const baseValores = {};
      if (baseTipo === "ventas") {
        aniosProyectados.forEach((anio) => {
          baseValores[anio] = ventasPorAnio[anio] ?? null;
        });
        if (aniosProyectados.some((a) => baseValores[a] === null || baseValores[a] === undefined)) {
          aviso = "La base aún no tiene proyección";
        }
      } else if (baseLineaObj) {
        const proyMap = {};
        (baseLineaObj.proyeccion || []).forEach(({ anio, valor }) => {
          proyMap[anio] = valor;
        });
        aniosProyectados.forEach((anio) => {
          baseValores[anio] = proyMap[anio] ?? null;
        });
        if (aniosProyectados.some((a) => baseValores[a] === null || baseValores[a] === undefined)) {
          aviso = "La base aún no tiene proyección";
        }
      } else if (baseTipo === "linea") {
        aviso = baseLinea ? "La base aún no tiene proyección" : "Selecciona una línea base";
      }

      aniosProyectados.forEach((anio) => {
        let pct = null;
        if (modoPct === "fijo") {
          const p = parseFloat(pctFijo);
          pct = !isNaN(p) ? p : null;
        } else if (modoPct === "historico") {
          pct = linea.ratios?.pct_medio_historico ?? null;
        } else if (modoPct === "por_anio") {
          const raw = pctsPorAnio[anio];
          const p = raw === "" || raw === undefined ? NaN : parseFloat(raw);
          pct = !isNaN(p) ? p : null;
        }
        const base = baseValores[anio];
        const valor = pct !== null && base !== null && base !== undefined ? (base * pct) / 100 : null;
        filas.push({ anio, valor, base: base ?? null });
      });
    }

    // % s/ventas estimado de cada fila
    filas.forEach((f) => {
      const ventas = ventasPorAnio[f.anio];
      f.pctVentas = f.valor !== null && f.valor !== undefined && ventas ? (f.valor / ventas) * 100 : null;
    });

    return { filas, aviso };
  }, [linea, tipo, metodo, tasaSeleccionada, crecimientos, aniosProyectados, valoresFijos, valorFijoModo, valorFijoBase, baseTipo, baseLinea, baseLineaObj, ventasPorAnio, ventasHistPorAnio, modoPct, pctFijo, pctsPorAnio, nAniosHist, tasasPorAnio, rampaInicial, rampaIncremento, ipcPct, ipcBase, ocultarValorFijoIpc, aplicarLimites, alpha, pxqUnidades, pxqPrecio]);

  // ---- Guardar / eliminar ----
  const buildLimites = () => {
    const mn = parseFloat(String(limiteMin).replace(",", "."));
    const mx = parseFloat(String(limiteMax).replace(",", "."));
    if (isNaN(mn) && isNaN(mx)) return null;
    return {
      min_pct: isNaN(mn) ? null : mn,
      max_pct: isNaN(mx) ? null : mx,
    };
  };

  const buildDriver = () => {
    if (tipo === "crecimiento") {
      const params = {
        metodo,
        tasa_manual: metodo === "manual" ? parseFloat(tasaManual) || 0 : null,
      };
      if (metodo === "por_anio") {
        const tasas = {};
        aniosProyectados.forEach((anio) => {
          const raw = tasasPorAnio[anio];
          const p = raw === "" || raw === undefined ? null : parseFloat(String(raw).replace(",", "."));
          if (p !== null && !isNaN(p)) tasas[String(anio)] = p;
        });
        params.tasas_por_anio = tasas;
      }
      if (metodo === "rampa") {
        params.rampa = {
          tasa_inicial: parseFloat(String(rampaInicial).replace(",", ".")) || 0,
          incremento_anual: parseFloat(String(rampaIncremento).replace(",", ".")) || 0,
        };
      }
      if (metodo === "ipc") {
        params.ipc_pct = parseFloat(String(ipcPct).replace(",", ".")) || 0;
        params.base_ipc = ipcBase === "media" ? "media" : "ultimo";
      }
      if (METODOS_TASA.has(metodo)) {
        const limites = buildLimites();
        if (limites) params.limites = limites;
      }
      return { tipo: "crecimiento", params };
    }
    if (tipo === "elasticidad") {
      const base = baseTipo === "ventas"
        ? { agregado: "VENTAS" }
        : { seccion: baseLinea?.seccion, nivel, codigo: baseLinea?.codigo };
      return {
        tipo: "elasticidad",
        params: { base, alpha: parseFloat(String(alpha).replace(",", ".")) || 0 },
      };
    }
    if (tipo === "pxq") {
      return {
        tipo: "pxq",
        params: {
          crecimiento_unidades_pct: parseFloat(String(pxqUnidades).replace(",", ".")) || 0,
          crecimiento_precio_pct: parseFloat(String(pxqPrecio).replace(",", ".")) || 0,
        },
      };
    }
    if (tipo === "desactivado") {
      return { tipo: "desactivado", params: {} };
    }
    if (tipo === "a_cero") {
      return { tipo: "a_cero", params: {} };
    }
    if (tipo === "valor_fijo") {
      if (valorFijoModo === "ipc" && !ocultarValorFijoIpc) {
        return {
          tipo: "valor_fijo",
          params: {
            modo: "ipc",
            valor_base: parseFloat(String(valorFijoBase).replace(",", ".")) || 0,
            ipc_pct: parseFloat(String(ipcPct).replace(",", ".")) || 0,
          },
        };
      }
      const valores = {};
      aniosProyectados.forEach((anio) => {
        const raw = valoresFijos[anio];
        const v = raw === "" || raw === undefined ? null : parseFloat(raw);
        if (v !== null && !isNaN(v)) valores[String(anio)] = v;
      });
      return { tipo: "valor_fijo", params: { valores } };
    }
    // porcentaje_de
    const base = baseTipo === "ventas"
      ? { agregado: "VENTAS" }
      : { seccion: baseLinea?.seccion, nivel, codigo: baseLinea?.codigo };
    const params = { base, modo: modoPct, pct: null, pcts_por_anio: null };
    if (modoPct === "fijo") params.pct = parseFloat(pctFijo) || 0;
    if (modoPct === "por_anio") {
      const pcts = {};
      aniosProyectados.forEach((anio) => {
        const raw = pctsPorAnio[anio];
        const p = raw === "" || raw === undefined ? null : parseFloat(raw);
        if (p !== null && !isNaN(p)) pcts[String(anio)] = p;
      });
      params.pcts_por_anio = pcts;
    }
    return { tipo: "porcentaje_de", params };
  };

  const handleSave = async () => {
    if (!tipo) {
      toast.error("Elige primero cómo quieres proyectar esta línea");
      return;
    }
    if ((tipo === "porcentaje_de" || tipo === "elasticidad") && baseTipo === "linea" && !baseLinea) {
      toast.error("Selecciona la línea base");
      return;
    }
    // En masivo la validez de "tendencia" depende de cada línea (histórico
    // propio): la valida el backend y devuelve errores por línea.
    if (!masivo && tipo === "crecimiento" && metodo === "tendencia" && nAniosHist < 3) {
      toast.error("La tendencia necesita al menos 3 años históricos");
      return;
    }
    // UX drivers: con solape al CREAR, primero la decisión explícita.
    if (hayConflicto && !conflicto) {
      setConflicto(buildDriver());
      return;
    }
    setSaving(true);
    try {
      await onSave(buildDriver());
      onOpenChange(false);
      // En masivo el toast de resultado (aplicadas/errores) lo emite onSave.
      if (!masivo) toast.success("Driver guardado");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Error al guardar el driver");
    } finally {
      setSaving(false);
    }
  };

  // UX drivers: resolución del conflicto — opcionalmente anula los drivers
  // solapados elegidos y después guarda el driver pendiente.
  const confirmarConflicto = async (anularIds) => {
    setSaving(true);
    try {
      if (anularIds?.length && onAnularDrivers) {
        await onAnularDrivers(anularIds);
      }
      await onSave(conflicto);
      onOpenChange(false);
      toast.success(
        anularIds?.length
          ? `Driver guardado — ${anularIds.length} driver${anularIds.length > 1 ? "s" : ""} solapado${anularIds.length > 1 ? "s" : ""} anulado${anularIds.length > 1 ? "s" : ""}`
          : "Driver guardado (la línea más específica manda)"
      );
      setConflicto(null);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Error al guardar el driver");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await onSave(null);
      onOpenChange(false);
      if (!masivo) toast.success("Driver eliminado");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Error al eliminar el driver");
    } finally {
      setDeleting(false);
    }
  };

  const copiarPrimerAnio = () => {
    const primero = valoresFijos[aniosProyectados[0]];
    if (primero === undefined || primero === "") return;
    const nuevos = {};
    aniosProyectados.forEach((a) => {
      nuevos[a] = primero;
    });
    setValoresFijos(nuevos);
  };

  if (!linea) return null;

  // UX drivers (sept-2026): solapes de jerarquía con otros drivers guardados
  // de la MISMA sección — ancestros (nivel más agregado cuyo código es
  // prefijo del de la línea) y descendientes (subcuentas con driver propio).
  const ESPEC_NIVEL = { "1D": 0, "3D": 1, MAX: 2 };
  const nivelLinea = linea.nivel || nivel;
  const seccionLinea = linea.seccion_origen;
  const solapes = masivo
    ? { ancestros: [], descendientes: [] }
    : {
        ancestros: (todosDrivers || []).filter(
          (dr) =>
            dr.seccion === seccionLinea &&
            dr.id !== linea.id &&
            ESPEC_NIVEL[dr.nivel] < ESPEC_NIVEL[nivelLinea] &&
            String(linea.codigo).startsWith(String(dr.codigo))
        ),
        descendientes: (todosDrivers || []).filter(
          (dr) =>
            dr.seccion === seccionLinea &&
            dr.id !== linea.id &&
            ESPEC_NIVEL[dr.nivel] > ESPEC_NIVEL[nivelLinea] &&
            String(dr.codigo).startsWith(String(linea.codigo))
        ),
      };
  // El diálogo de decisión solo salta al CREAR un driver nuevo con solape
  // (editar uno existente no re-pregunta: el solape ya se aceptó en su día).
  const hayConflicto =
    !readOnly && !linea.driver &&
    (solapes.ancestros.length > 0 || solapes.descendientes.length > 0);

  // Líneas candidatas a base (de las 3 secciones, excluyendo la propia línea)
  const candidatasBase = baseCache
    ? Object.entries(baseCache).flatMap(([seccion, lineasSec]) =>
        (lineasSec || [])
          .filter((l) => !(seccion === linea.seccion_origen && l.codigo === linea.codigo) && l.id !== linea.id)
          .map((l) => ({ ...l, seccion }))
      )
    : [];

  // Selector de base (compartido por "% de otra línea" y "Elasticidad")
  const selectorBase = (
    <div className="space-y-2">
      <Label>Base</Label>
      <div className="space-y-2">
        <label className={cn("flex items-center gap-2 rounded-md border p-2.5", baseTipo === "ventas" && "border-primary bg-primary/5", !readOnly && "cursor-pointer")}>
          <input
            type="radio"
            name="baseTipo"
            className="accent-[hsl(var(--primary))]"
            checked={baseTipo === "ventas"}
            onChange={() => setBaseTipo("ventas")}
            disabled={readOnly}
          />
          <span className="text-sm">Ventas totales (partida 1)</span>
        </label>
        <label className={cn("flex items-center gap-2 rounded-md border p-2.5", baseTipo === "linea" && "border-primary bg-primary/5", !readOnly && "cursor-pointer")}>
          <input
            type="radio"
            name="baseTipo"
            className="accent-[hsl(var(--primary))]"
            checked={baseTipo === "linea"}
            onChange={() => setBaseTipo("linea")}
            disabled={readOnly}
          />
          <span className="text-sm">Otra línea…</span>
        </label>
      </div>

      {baseTipo === "linea" && (
        <Popover open={basePickerOpen} onOpenChange={setBasePickerOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              disabled={readOnly}
              className="w-full justify-between font-normal"
            >
              {baseLinea ? (
                <span className="truncate">
                  {baseLinea.codigo} — {baseLinea.nombre}
                </span>
              ) : (
                <span className="text-muted-foreground">
                  {baseCacheLoading ? "Cargando líneas…" : "Buscar línea base…"}
                </span>
              )}
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
            <Command>
              <CommandInput placeholder="Buscar por código o nombre…" />
              <CommandList>
                <CommandEmpty>
                  {baseCacheLoading ? "Cargando…" : "Sin resultados"}
                </CommandEmpty>
                {["ingresos", "otros_ingresos", "gastos"].map((sec) => {
                  const items = candidatasBase.filter((l) => l.seccion === sec);
                  if (items.length === 0) return null;
                  return (
                    <CommandGroup key={sec} heading={seccionesLabel[sec]}>
                      {items.map((l) => (
                        <CommandItem
                          key={`${sec}-${l.id}`}
                          value={`${l.codigo} ${l.nombre}`}
                          onSelect={() => {
                            setBaseLinea({ seccion: sec, codigo: l.codigo, nombre: l.nombre });
                            setBasePickerOpen(false);
                          }}
                        >
                          <Check
                            className={cn(
                              "mr-2 h-4 w-4",
                              baseLinea?.codigo === l.codigo && baseLinea?.seccion === sec
                                ? "opacity-100"
                                : "opacity-0"
                            )}
                          />
                          <span className="font-mono text-xs mr-2">{l.codigo}</span>
                          <span className="truncate">{l.nombre}</span>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  );
                })}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      )}
    </div>
  );

  // Ejemplo dinámico para elasticidad: "si la base crece 10% y α=0,5, esta línea crece 5%"
  const alphaNum = parseFloat(String(alpha).replace(",", "."));
  const ejemploAlpha = !isNaN(alphaNum)
    ? `Si la base crece 10% y α=${new Intl.NumberFormat("es-ES", { maximumFractionDigits: 2 }).format(alphaNum)}, esta línea crece ${formatPctPlain(10 * alphaNum)}.`
    : null;

  // Efecto combinado de Volumen × Precio
  const pxqU = parseFloat(String(pxqUnidades).replace(",", ".")) || 0;
  const pxqP = parseFloat(String(pxqPrecio).replace(",", ".")) || 0;
  const pxqCombinado = ((1 + pxqU / 100) * (1 + pxqP / 100) - 1) * 100;

  // R-7: en drivers relativos (porcentaje_de / elasticidad) el preview muestra
  // el valor absoluto de la base/origen junto al resultado.
  const esDriverRelativo = tipo === "porcentaje_de" || tipo === "elasticidad";
  const etiquetaOrigen = baseTipo === "ventas"
    ? "VENTAS"
    : (baseLinea ? `${baseLinea.codigo}` : "Base");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[640px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {masivo
              ? "Aplicar driver a varias líneas"
              : readOnly
                ? "Driver de proyección"
                : "Configurar driver de proyección"}
          </DialogTitle>
          <DialogDescription>
            {masivo
              ? `Se aplicará el mismo driver a ${masivoCount} línea${masivoCount === 1 ? "" : "s"} seleccionada${masivoCount === 1 ? "" : "s"}.`
              : `${linea.codigo} — ${linea.nombre}`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {/* UX drivers (sept-2026): CONTEXTO antes de tocar nada — quién
              gobierna hoy estas cuentas. Nadie configura a ciegas. */}
          {!masivo && solapes.ancestros.length > 0 && (
            <div className="rounded-md border border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/30 px-3 py-2 text-[12px] text-amber-800 dark:text-amber-300">
              <b>Ojo:</b> esta línea pertenece a{" "}
              {solapes.ancestros.map((dr, i) => (
                <span key={dr.id}>
                  {i > 0 && " y a "}
                  <b>{dr.codigo}</b> ({dr.descripcion})
                </span>
              ))}
              . Si guardas un driver aquí, estas cuentas <b>dejarán de seguirlo</b> y
              el resto de la línea general seguirá igual.
            </div>
          )}
          {!masivo && solapes.descendientes.length > 0 && (
            <div className="rounded-md border border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/30 px-3 py-2 text-[12px] text-amber-800 dark:text-amber-300">
              <b>Ojo:</b> dentro de esta línea hay{" "}
              <b>{solapes.descendientes.length} subcuenta{solapes.descendientes.length > 1 ? "s" : ""} con driver propio</b>{" "}
              ({solapes.descendientes.slice(0, 4).map((dr) => dr.codigo).join(", ")}
              {solapes.descendientes.length > 4 ? "…" : ""}). Este driver solo
              aplicará a las subcuentas que <b>no</b> tengan el suyo.
            </div>
          )}
          {/* Selector de tipo */}
          <TipoDriverCards value={tipo} onChange={setTipo} disabled={readOnly} tiposPermitidos={tiposPermitidos} />

          {!tipo && !readOnly && (
            <p className="text-sm text-muted-foreground">
              Esta línea no tiene driver propio: lo que ves en la tabla es la
              proyección {linea?.origen_proyeccion ? "heredada" : "automática"}.
              Elige arriba cómo quieres proyectarla para configurarla.
            </p>
          )}

          <Separator />

          {/* ----- Formulario CRECIMIENTO ----- */}
          {tipo === "crecimiento" && (
            <div className="space-y-4">
              <div>
                <Label className="text-xs text-muted-foreground uppercase tracking-wider">
                  % calculados de cada método
                </Label>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {metodosCrecimiento.filter((m) => m.id !== "manual").map((m) => {
                    const val = crecimientos?.[m.id];
                    return (
                      <Badge
                        key={m.id}
                        variant="outline"
                        className={cn(
                          "gap-1 font-normal",
                          metodo === m.id && "border-primary bg-primary/10"
                        )}
                        title={m.descripcion}
                      >
                        <span className="text-muted-foreground">{m.label}</span>
                        <span
                          className={cn(
                            "font-semibold",
                            val > 0 ? "text-emerald-600 dark:text-emerald-400" : val < 0 ? "text-rose-600 dark:text-rose-400" : ""
                          )}
                        >
                          {formatPctSigned(val)}
                        </span>
                      </Badge>
                    );
                  })}
                </div>
              </div>

              <div className="space-y-2">
                <Label>Método de proyección</Label>
                <Select value={metodo} onValueChange={setMetodo} disabled={readOnly}>
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectLabel>Histórico</SelectLabel>
                      {metodosHistoricos.map((m) => {
                        const esTendencia = m.id === "tendencia";
                        const val = esTendencia ? null : crecimientos?.[m.id];
                        const disabled = esTendencia
                          ? nAniosHist < 3
                          : val === null || val === undefined;
                        return (
                          <SelectItem key={m.id} value={m.id} disabled={disabled}>
                            {m.label} — {m.descripcion}
                            {!esTendencia && val !== null && val !== undefined
                              ? ` (${formatPctSigned(val)})`
                              : ""}
                            {esTendencia && nAniosHist < 3 ? " (necesita ≥3 años)" : ""}
                          </SelectItem>
                        );
                      })}
                    </SelectGroup>
                    <SelectGroup>
                      <SelectLabel>Definido por ti</SelectLabel>
                      {metodosDefinidos.map((m) => (
                        <SelectItem key={m.id} value={m.id}>
                          {m.label} — {m.descripcion}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>

              {metodo === "manual" && (
                <div className="space-y-2">
                  <Label>Tasa de crecimiento manual (%)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={tasaManual}
                    onChange={(e) => setTasaManual(e.target.value)}
                    placeholder="Ej: 5.0"
                    disabled={readOnly}
                  />
                </div>
              )}

              {metodo === "por_anio" && (
                <div className="space-y-2">
                  <Label>Tasa por año (%)</Label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {aniosProyectados.map((anio) => (
                      <div key={anio} className="space-y-1">
                        <Label className="text-xs text-muted-foreground">{anio} (%)</Label>
                        <Input
                          type="number"
                          step="0.1"
                          value={tasasPorAnio[anio] ?? ""}
                          onChange={(e) =>
                            setTasasPorAnio((prev) => ({ ...prev, [anio]: e.target.value }))
                          }
                          placeholder="hereda"
                          disabled={readOnly}
                        />
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Un año vacío hereda la tasa del año anterior.
                  </p>
                </div>
              )}

              {metodo === "rampa" && (
                <div className="space-y-2">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Tasa inicial (%)</Label>
                      <Input
                        type="number"
                        step="0.1"
                        value={rampaInicial}
                        onChange={(e) => setRampaInicial(e.target.value)}
                        placeholder="Ej: 10"
                        disabled={readOnly}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Incremento anual (pp)</Label>
                      <Input
                        type="number"
                        step="0.1"
                        value={rampaIncremento}
                        onChange={(e) => setRampaIncremento(e.target.value)}
                        placeholder="Ej: 1"
                        disabled={readOnly}
                      />
                    </div>
                  </div>
                  {tasasRampa && tasasRampa.length > 0 && (
                    <p className="text-xs text-primary font-medium">
                      {tasasRampa.map((t) => formatPctPlain(t.tasa)).join(" → ")}
                    </p>
                  )}
                </div>
              )}

              {metodo === "ipc" && (
                <div className="space-y-2">
                  <Label>IPC anual (%)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={ipcPct}
                    onChange={(e) => setIpcPct(e.target.value)}
                    placeholder="Ej: 2"
                    disabled={readOnly}
                  />
                  <Label className="pt-1 block">Base de arranque</Label>
                  <Select value={ipcBase} onValueChange={setIpcBase} disabled={readOnly}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ultimo">Último año real</SelectItem>
                      <SelectItem value="media">Media del histórico</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    {ipcBase === "media"
                      ? "Arranca de la media del histórico × (1+IPC)^k."
                      : "Arranca del último real × (1+IPC)^k."}
                  </p>
                </div>
              )}

              {metodo === "tendencia" && (
                <div className="rounded-md border bg-muted/30 p-3 text-sm space-y-1.5">
                  <p>Ajusta una recta a tu histórico y la prolonga.</p>
                  {nAniosHist < 3 && (
                    <p className="flex items-center gap-1.5 text-xs text-primary">
                      <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                      Necesita al menos 3 años históricos con dato (tienes {nAniosHist}).
                    </p>
                  )}
                </div>
              )}

              {/* Límites de seguridad (solo métodos de tasa) */}
              {METODOS_TASA.has(metodo) && (
                <Collapsible open={limitesOpen} onOpenChange={setLimitesOpen}>
                  <CollapsibleTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="px-0 text-xs text-muted-foreground hover:bg-transparent gap-1.5"
                    >
                      <Shield className="h-3.5 w-3.5" />
                      Límites de seguridad
                      <ChevronDown
                        className={cn("h-3.5 w-3.5 transition-transform", limitesOpen && "rotate-180")}
                      />
                    </Button>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <div className="space-y-2 pt-2">
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs text-muted-foreground">Mínimo (%)</Label>
                          <Input
                            type="number"
                            step="0.1"
                            value={limiteMin}
                            onChange={(e) => setLimiteMin(e.target.value)}
                            placeholder="Sin límite"
                            disabled={readOnly}
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs text-muted-foreground">Máximo (%)</Label>
                          <Input
                            type="number"
                            step="0.1"
                            value={limiteMax}
                            onChange={(e) => setLimiteMax(e.target.value)}
                            placeholder="Sin límite"
                            disabled={readOnly}
                          />
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Acota la tasa aplicada cada año: evita proyectar tasas históricas anómalas.
                      </p>
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              )}
            </div>
          )}

          {/* ----- Formulario VALOR FIJO ----- */}
          {tipo === "valor_fijo" && (
            <div className="space-y-3">
              {/* Sub-modo: importes por año (clásico) o valor base + IPC.
                  F3: en amortización (ocultarValorFijoIpc) solo "importes por año". */}
              {!ocultarValorFijoIpc && (
                <div className="space-y-2">
                  <Label>Modo</Label>
                  <Select value={valorFijoModo} onValueChange={setValorFijoModo} disabled={readOnly}>
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fijo">Importes por año</SelectItem>
                      <SelectItem value="ipc">Valor base + IPC</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              {valorFijoModo === "ipc" && !ocultarValorFijoIpc ? (
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Valor base (€)</Label>
                    <Input
                      type="number"
                      step="any"
                      value={valorFijoBase}
                      onChange={(e) => setValorFijoBase(e.target.value)}
                      placeholder="0"
                      disabled={readOnly}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">IPC anual (%)</Label>
                    <Input
                      type="number"
                      step="any"
                      value={ipcPct}
                      onChange={(e) => setIpcPct(e.target.value)}
                      placeholder="2"
                      disabled={readOnly}
                    />
                  </div>
                  <p className="col-span-2 text-xs text-muted-foreground">
                    Cada año proyectado = valor base × (1+IPC)<sup>k</sup>: el primer
                    año es el valor base y crece con el IPC en los siguientes.
                  </p>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between">
                    <Label>Importe por año proyectado (€)</Label>
                    {!readOnly && aniosProyectados.length > 1 && (
                      <Button variant="outline" size="sm" className="h-7 gap-1.5 text-xs" onClick={copiarPrimerAnio}>
                        <Copy className="h-3 w-3" />
                        Copiar primer año a todos
                      </Button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {aniosProyectados.map((anio) => (
                      <div key={anio} className="space-y-1">
                        <Label className="text-xs text-muted-foreground">{anio}</Label>
                        <Input
                          type="number"
                          step="any"
                          value={valoresFijos[anio] ?? ""}
                          onChange={(e) => setValoresFijos((prev) => ({ ...prev, [anio]: e.target.value }))}
                          placeholder="0"
                          disabled={readOnly}
                        />
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {/* ----- Formulario % DE OTRA LÍNEA ----- */}
          {tipo === "porcentaje_de" && (
            <div className="space-y-4">
              {selectorBase}

              <div className="space-y-2">
                <Label>Modo</Label>
                <Select value={modoPct} onValueChange={setModoPct} disabled={readOnly}>
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fijo">% fijo</SelectItem>
                    <SelectItem value="historico">% histórico</SelectItem>
                    <SelectItem value="por_anio">% por año</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {modoPct === "fijo" && (
                <div className="space-y-2">
                  <Label>Porcentaje (%)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={pctFijo}
                    onChange={(e) => setPctFijo(e.target.value)}
                    placeholder="Ej: 40"
                    disabled={readOnly}
                  />
                </div>
              )}

              {modoPct === "historico" && (
                <div className="rounded-md border bg-muted/30 p-3 text-sm">
                  {linea.ratios?.pct_medio_historico !== null && linea.ratios?.pct_medio_historico !== undefined ? (
                    <span>
                      Media histórica:{" "}
                      <span className="font-semibold text-primary">
                        {formatPctPlain(linea.ratios.pct_medio_historico)}
                      </span>
                    </span>
                  ) : (
                    <span className="text-muted-foreground">
                      El % histórico lo calcula el servidor al guardar.
                    </span>
                  )}
                </div>
              )}

              {modoPct === "por_anio" && (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {aniosProyectados.map((anio) => (
                    <div key={anio} className="space-y-1">
                      <Label className="text-xs text-muted-foreground">{anio} (%)</Label>
                      <Input
                        type="number"
                        step="0.1"
                        value={pctsPorAnio[anio] ?? ""}
                        onChange={(e) => setPctsPorAnio((prev) => ({ ...prev, [anio]: e.target.value }))}
                        placeholder="0"
                        disabled={readOnly}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ----- Formulario ELASTICIDAD ----- */}
          {tipo === "elasticidad" && (
            <div className="space-y-4">
              {selectorBase}

              <div className="space-y-2">
                <Label>Elasticidad α</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={alpha}
                  onChange={(e) => setAlpha(e.target.value)}
                  placeholder="Ej: 0,5"
                  disabled={readOnly}
                />
                {ejemploAlpha && (
                  <p className="text-xs text-muted-foreground">{ejemploAlpha}</p>
                )}
              </div>
            </div>
          )}

          {/* ----- Formulario VOLUMEN × PRECIO ----- */}
          {tipo === "pxq" && (
            <div className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Crecimiento unidades (%/año)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={pxqUnidades}
                    onChange={(e) => setPxqUnidades(e.target.value)}
                    placeholder="Ej: 3"
                    disabled={readOnly}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Crecimiento precio (%/año)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={pxqPrecio}
                    onChange={(e) => setPxqPrecio(e.target.value)}
                    placeholder="Ej: 2"
                    disabled={readOnly}
                  />
                </div>
              </div>
              <p className="text-xs text-primary font-medium">
                Efecto combinado: {formatPctSigned(pxqCombinado)} anual
              </p>
            </div>
          )}

          {tipo === "desactivado" && (
            <div className="rounded-md border bg-muted/30 p-3 text-sm space-y-1.5">
              <p className="flex items-center gap-1.5 font-medium">
                <Ban className="h-4 w-4 shrink-0 text-muted-foreground" />
                Línea desactivada
              </p>
              <p className="text-xs text-muted-foreground">
                No se proyecta con ninguna hipótesis: se mantiene <strong>plana</strong> en
                el valor del último año real durante todo el horizonte. No desaparece del
                total de su partida (sigue sumando su último importe), solo deja de crecer.
              </p>
            </div>
          )}

          {tipo === "a_cero" && (
            <div className="rounded-md border bg-muted/30 p-3 text-sm space-y-1.5">
              <p className="flex items-center gap-1.5 font-medium">
                <CircleOff className="h-4 w-4 shrink-0 text-muted-foreground" />
                Línea a cero
              </p>
              <p className="text-xs text-muted-foreground">
                La línea vale <strong>0 €</strong> en todos los años proyectados
                (p.ej. un gasto o ingreso que no se repetirá). Sigue visible en la
                tabla y su partida simplemente deja de incluirla a futuro.
              </p>
            </div>
          )}

          {/* El preview carece de sentido en masivo (líneas heterogéneas). */}
          {!masivo && (
          <>
          <Separator />

          {/* ----- PREVIEW ----- */}
          <div className="p-4 rounded-lg bg-muted/30 space-y-3">
            <Label className="text-xs text-muted-foreground uppercase tracking-wider">
              Preview proyección
            </Label>
            {preview.aviso && (
              <div className="flex items-center gap-2 text-xs text-primary">
                <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                <span>{preview.aviso}</span>
              </div>
            )}
            <div className="rounded border overflow-hidden bg-background">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead className="h-8 text-xs">Año</TableHead>
                    {esDriverRelativo && (
                      <TableHead className="h-8 text-xs text-right" title={`Valor de la base (${etiquetaOrigen})`}>
                        Origen ({etiquetaOrigen})
                      </TableHead>
                    )}
                    <TableHead className="h-8 text-xs text-right">Importe</TableHead>
                    <TableHead className="h-8 text-xs text-right">% s/ventas</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {preview.filas.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={esDriverRelativo ? 4 : 3} className="text-center text-xs text-muted-foreground py-4">
                        Sin datos para previsualizar
                      </TableCell>
                    </TableRow>
                  ) : (
                    preview.filas.map((f) => (
                      <TableRow key={f.anio} className="bg-primary/5">
                        <TableCell className="py-1.5 text-sm font-medium">{f.anio}</TableCell>
                        {esDriverRelativo && (
                          <TableCell className="py-1.5 text-right font-mono text-xs text-muted-foreground">
                            {f.base !== null && f.base !== undefined ? formatCurrency(f.base) : "—"}
                          </TableCell>
                        )}
                        <TableCell className="py-1.5 text-right font-mono text-sm font-semibold">
                          {formatCurrency(f.valor)}
                        </TableCell>
                        <TableCell className="py-1.5 text-right text-xs text-primary">
                          {f.pctVentas !== null ? `${formatPctPlain(f.pctVentas)} s/ventas` : "—"}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
          </>
          )}
        </div>

        {conflicto ? (
          /* UX drivers (sept-2026): DECISIÓN EXPLÍCITA del solape — nada se
             pisa en silencio. */
          <div className="rounded-md border-2 border-amber-400 bg-amber-50 dark:bg-amber-950/30 p-3 space-y-2">
            <p className="text-sm font-semibold">¿Cómo resolvemos el solape?</p>
            <div className="flex flex-col gap-2">
              <Button onClick={() => confirmarConflicto([])} disabled={saving} className="justify-start gap-2">
                <Save className="h-4 w-4" />
                {solapes.ancestros.length > 0
                  ? "Guardar así — esta línea manda y el resto sigue con su driver general (recomendado)"
                  : "Guardar así — las subcuentas con driver propio siguen mandando (recomendado)"}
              </Button>
              {solapes.ancestros.length > 0 && (
                <Button variant="outline" disabled={saving}
                  onClick={() => confirmarConflicto(solapes.ancestros.map((dr) => dr.id))}
                  className="justify-start gap-2 text-amber-700 dark:text-amber-400">
                  <Trash2 className="h-4 w-4" />
                  Anular el driver de {solapes.ancestros.map((dr) => dr.codigo).join(", ")} y guardar
                </Button>
              )}
              {solapes.descendientes.length > 0 && (
                <Button variant="outline" disabled={saving}
                  onClick={() => confirmarConflicto(solapes.descendientes.map((dr) => dr.id))}
                  className="justify-start gap-2 text-amber-700 dark:text-amber-400">
                  <Trash2 className="h-4 w-4" />
                  Anular los {solapes.descendientes.length} driver{solapes.descendientes.length > 1 ? "s" : ""} de subcuenta y que esta línea gobierne todo
                </Button>
              )}
              <Button variant="ghost" disabled={saving} onClick={() => setConflicto(null)}
                className="justify-start">
                ← Volver sin guardar
              </Button>
            </div>
          </div>
        ) : (
        <DialogFooter className="gap-2 sm:gap-0">
          {/* Por-línea: borrar el driver de esa línea. Masivo: quitar el driver
              de todas las seleccionadas (aplica null en bloque). */}
          {!readOnly && (masivo || linea.driver) && (
            <Button
              variant="outline"
              onClick={handleDelete}
              disabled={deleting || saving}
              className="gap-1.5 text-rose-600 hover:text-rose-700 dark:text-rose-400 sm:mr-auto"
            >
              <Trash2 className="h-4 w-4" />
              {masivo ? "Quitar driver" : "Eliminar driver"}
            </Button>
          )}
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {readOnly ? "Cerrar" : "Cancelar"}
          </Button>
          {!readOnly && (
            <Button onClick={handleSave} disabled={saving || deleting || !tipo} className="gap-2">
              <Save className="h-4 w-4" />
              {masivo ? "Aplicar driver" : "Guardar driver"}
            </Button>
          )}
        </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Componente principal
// ---------------------------------------------------------------------------

export default function TablaDrivers({
  empresaId,
  ejercicios,
  seccionPyg,
  titulo,
  descripcion,
  aniosProyectar = 1,
  invertirSemantica = false,
  readOnly = false,
  // R-10: filtro opcional por prefijo(s) de código de línea. Sin este prop el
  // comportamiento es idéntico al de siempre (se muestran todas las líneas).
  // Acepta un string ("60") o un array de strings (["60", "62"]).
  filtroCodigos = null,
  // Bloqueo por cuenta desde el CONTENEDOR (no del motor): las líneas cuyo
  // código empiece por uno de estos prefijos se muestran pero no se pueden
  // drivear, con `textoBloqueo` como motivo. Reutiliza el patrón existente
  // `bloqueada_por` (badge + checkbox deshabilitado + sin click). Ej.: la 662
  // en Financiación → "Se informa en Pool bancario" (los intereses de la deuda
  // arrastrada salen del cuadro del préstamo existente, no de un driver).
  cuentasBloqueadas = null,
  textoBloqueo = "Gestionada en otra pestaña",
  // Filas INFORMATIVAS: cuentas que deben verse siempre para mantener la
  // estructura contable completa aunque no tengan datos ni sean proyectables
  // (email del cliente 2026-08-19: el grupo 66 entero, 661-669, a guiones).
  // Array de {codigo, nombre}; solo se añaden las que no existan ya como línea.
  cuentasInformativas = null,
  // Restringe los tipos de driver ofrecidos en el editor (p.ej. ["valor_fijo"]
  // para la amortización editable desde CAPEX). Sin él, todos los tipos.
  tiposDriverPermitidos = null,
  // F3: oculta el modo "Valor base + IPC" del driver valor_fijo (amortización).
  ocultarValorFijoIpc = false,
  // Callback opcional tras guardar/borrar un driver: el padre puede invalidar
  // datos derivados de la proyección (p.ej. el `resultado` que alimenta las
  // tarjetas de amortización/inmovilizado en CAPEX).
  onDriverSaved = null,
  // Nivel de detalle CONTROLADO desde la página: props para usos donde el
  // selector vive fuera de la tabla. Desde el 2026-09-30 (queja Pau: "el
  // seleccionable del nivel debería estar en cada driver") las pestañas de
  // Proyección ya NO lo controlan: cada tabla lleva su selector propio.
  nivel: nivelControlado = null,
  onNivelChange = null,
  // Clave de localStorage para RECORDAR el nivel elegido de ESTA tabla
  // (por empresa y pestaña, p.ej. "finreport:nivel:<empresaId>:capex").
  // Solo aplica al selector propio (sin nivel controlado). Sin storageKey,
  // el nivel vive en la sesión, como siempre.
  storageKey = null,
  // Punto 1 de Héctor (oct-2026): en las tablas FILTRADAS por grupo de cuenta
  // (Gastos 60/61/62/64, CAPEX 68, Financiación 66) el nivel "1 Dígito" no
  // tiene sentido — la línea agregada es el grupo "6" entero y el filtro por
  // prefijos la dejaba VACÍA ("no aparece cap dada"). Estas tablas pasan el
  // subconjunto de niveles con sentido (p.ej. ["3D","MAX"]); un nivel
  // recordado fuera del subconjunto se corrige al primero permitido.
  nivelesPermitidos = null,
}) {
  const [nivelInterno, setNivelInterno] = useState(() => {
    if (storageKey) {
      try {
        const v = localStorage.getItem(storageKey);
        if (["1D", "3D", "MAX"].includes(v)) return v;
      } catch {
        /* almacenamiento no disponible: el nivel vive solo en la sesión */
      }
    }
    return "3D";
  });
  // CANDADO (Héctor 6-oct, "habría que revisar todas"): una tabla FILTRADA
  // por prefijos nunca puede ofrecer 1 dígito — la línea agregada es el grupo
  // entero y el filtro la dejaría vacía. Si el contenedor no pasa
  // nivelesPermitidos pero sí filtroCodigos, se limita solo.
  const nivelesEfectivos =
    nivelesPermitidos || (filtroCodigos ? ["3D", "MAX"] : null);
  const nivelCrudo = nivelControlado || nivelInterno;
  const nivel = nivelesEfectivos && !nivelesEfectivos.includes(nivelCrudo)
    ? nivelesEfectivos[0]
    : nivelCrudo;
  const setNivel =
    onNivelChange ||
    ((v) => {
      setNivelInterno(v);
      if (storageKey) {
        try {
          localStorage.setItem(storageKey, v);
        } catch {
          /* almacenamiento no disponible */
        }
      }
    });
  const [lineas, setLineas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLinea, setSelectedLinea] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  // F2A: selección múltiple para aplicar el mismo driver en bloque.
  const [seleccionadas, setSeleccionadas] = useState(() => new Set());
  const [masivoOpen, setMasivoOpen] = useState(false);
  const [baseCache, setBaseCache] = useState(null);
  const [baseCacheLoading, setBaseCacheLoading] = useState(false);
  const baseCacheNivelRef = useRef(null);
  // Añadir línea nueva (crea una cuenta con saldo 0 que aparece como línea proyectable).
  const [addLineaOpen, setAddLineaOpen] = useState(false);
  const [nuevoCodigo, setNuevoCodigo] = useState("");
  const [nuevoNombre, setNuevoNombre] = useState("");
  const [creandoLinea, setCreandoLinea] = useState(false);
  // W19.4/5: borrar/renombrar cuentas de alta manual desde la propia tabla.
  // `cuentaBorrando`/`cuentaRenombrando` = {cuenta_id, ejercicio_id, codigo, nombre}.
  const [cuentaBorrando, setCuentaBorrando] = useState(null);
  const [cuentaRenombrando, setCuentaRenombrando] = useState(null);
  const [nombreNuevo, setNombreNuevo] = useState("");
  const [accionandoCuenta, setAccionandoCuenta] = useState(false);
  // Punto 2 (Héctor oct-2026): alias de una línea agregada {linea, nombre}.
  const [aliasEditando, setAliasEditando] = useState(null);
  // UX drivers (sept-2026): drivers guardados de TODOS los niveles (para el
  // aviso de solape del diálogo y los badges); subcuentas desplegables
  // inline (sin cambiar el zoom global); resolución de líneas parciales.
  const [todosDrivers, setTodosDrivers] = useState([]);
  const [expandidas, setExpandidas] = useState(() => new Set());
  const [lineasMax, setLineasMax] = useState(null);
  const [cargandoSub, setCargandoSub] = useState(false);
  const [resolverPendiente, setResolverPendiente] = useState(null); // {linea, modo, ids}

  const aniosHistoricos = useMemo(() => {
    return ejercicios.map((e) => e.anio).sort((a, b) => a - b);
  }, [ejercicios]);

  const ultimoAnio = aniosHistoricos.length > 0 ? aniosHistoricos[aniosHistoricos.length - 1] : new Date().getFullYear();

  const aniosProyectados = useMemo(
    () => Array.from({ length: aniosProyectar }, (_, i) => ultimoAnio + 1 + i),
    [aniosProyectar, ultimoAnio]
  );

  // Nº total de columnas de la tabla (para filas de mensaje a todo lo ancho).
  // La columna de selección solo existe en modo edición (permiteSeleccion).
  const colSpanTotal =
    (!readOnly ? 1 : 0) + 2 + aniosHistoricos.length + aniosProyectados.length + 2;

  useEffect(() => {
    loadLineas();
    // Los ids de línea cambian al cambiar de nivel/sección: limpia la selección.
    setSeleccionadas(new Set());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresaId, nivel, seccionPyg]);

  const loadLineas = async () => {
    try {
      setLoading(true);
      const response = await getProyeccionLineas(empresaId, seccionPyg, nivel);
      setLineas(response.data || []);
      // Drivers de todos los niveles (aviso de solapes) + subcuentas
      // desplegadas: se refrescan junto a las líneas para no quedar stale.
      try {
        const drs = await getProyeccionDrivers(empresaId);
        setTodosDrivers(drs.data?.drivers || []);
      } catch {
        setTodosDrivers([]);
      }
      if (nivel !== "MAX" && lineasMax) {
        try {
          const sub = await getProyeccionLineas(empresaId, seccionPyg, "MAX");
          setLineasMax(sub.data || []);
        } catch {
          /* las sub-filas mantienen lo último cargado */
        }
      }
    } catch (error) {
      console.error("Error loading lineas:", error);
      setLineas([]);
    } finally {
      setLoading(false);
    }
  };

  // Subcuentas inline: carga perezosa de las líneas MAX de la sección la
  // primera vez que se despliega una línea.
  const toggleExpand = async (codigo) => {
    const nueva = new Set(expandidas);
    if (nueva.has(codigo)) {
      nueva.delete(codigo);
      setExpandidas(nueva);
      return;
    }
    nueva.add(codigo);
    setExpandidas(nueva);
    if (!lineasMax && nivel !== "MAX") {
      setCargandoSub(true);
      try {
        const sub = await getProyeccionLineas(empresaId, seccionPyg, "MAX");
        setLineasMax(sub.data || []);
      } catch {
        toast.error("Error al cargar las subcuentas");
      } finally {
        setCargandoSub(false);
      }
    }
  };

  // UX drivers: anular una lista de drivers por id (los solapados que el
  // usuario decide quitar en el diálogo de conflicto o en "Resolver").
  const anularDrivers = async (ids) => {
    for (const id of ids || []) {
      await saveProyeccionLinea(empresaId, id, null);
    }
  };

  // "Resolver" de una línea con driver parcial: anula los drivers elegidos
  // y recarga. `resolverPendiente` = {linea, ids, texto} (confirmación).
  const ejecutarResolver = async () => {
    if (!resolverPendiente) return;
    setAccionandoCuenta(true);
    try {
      await anularDrivers(resolverPendiente.ids);
      toast.success("Solape resuelto");
      setResolverPendiente(null);
      await loadLineas();
      baseCacheNivelRef.current = null;
      setBaseCache(null);
      if (onDriverSaved) await onDriverSaved();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Error al resolver el solape");
    } finally {
      setAccionandoCuenta(false);
    }
  };

  // Cargar caché de líneas de las 3 secciones al abrir el dialog (para "% de otra línea")
  const loadBaseCache = useCallback(async () => {
    if (baseCacheNivelRef.current === nivel && baseCache) return;
    setBaseCacheLoading(true);
    try {
      const [ing, otros, gas] = await Promise.all([
        getProyeccionLineas(empresaId, "ingresos", nivel),
        getProyeccionLineas(empresaId, "otros_ingresos", nivel),
        getProyeccionLineas(empresaId, "gastos", nivel),
      ]);
      setBaseCache({
        ingresos: ing.data || [],
        otros_ingresos: otros.data || [],
        gastos: gas.data || [],
      });
      baseCacheNivelRef.current = nivel;
    } catch (error) {
      console.error("Error loading base cache:", error);
    } finally {
      setBaseCacheLoading(false);
    }
  }, [empresaId, nivel, baseCache]);

  const handleConfigClick = (linea) => {
    if (readOnly && !linea.driver) return;
    setSelectedLinea({ ...linea, seccion_origen: seccionPyg });
    setDialogOpen(true);
    loadBaseCache();
  };

  const handleSaveLinea = async (driver) => {
    await saveProyeccionLinea(empresaId, selectedLinea.id, driver);
    // Recargar toda la sección: un driver puede afectar a líneas dependientes
    await loadLineas();
    // Invalidar caché de bases (las proyecciones han cambiado)
    baseCacheNivelRef.current = null;
    setBaseCache(null);
    // Avisar al padre: el resultado de la proyección ha cambiado.
    if (onDriverSaved) await onDriverSaved();
  };

  // F2A: aplica (o quita, con driver=null) el mismo driver a las líneas
  // seleccionadas. Reutiliza el mismo mecanismo de recarga que el flujo por-línea.
  const handleSaveMasivo = async (driver) => {
    const ids = [...seleccionadas];
    const { data } = await saveProyeccionLineasMasivo(empresaId, ids, driver);
    const aplicadas = data?.aplicadas ?? 0;
    const errores = data?.errores || [];
    if (errores.length > 0) {
      toast.warning(
        `${aplicadas} línea${aplicadas === 1 ? "" : "s"} actualizada${aplicadas === 1 ? "" : "s"}, ${errores.length} con error`,
        { description: errores.map((e) => `${e.linea_id}: ${e.detalle}`).join("\n") }
      );
    } else {
      toast.success(
        driver === null
          ? `Driver quitado de ${aplicadas} línea${aplicadas === 1 ? "" : "s"}`
          : `Driver aplicado a ${aplicadas} línea${aplicadas === 1 ? "" : "s"}`
      );
    }
    // Mismo mecanismo de recarga que tras guardar una línea.
    await loadLineas();
    baseCacheNivelRef.current = null;
    setBaseCache(null);
    setSeleccionadas(new Set());
    if (onDriverSaved) await onDriverSaved();
  };

  // Añadir línea: gating. En cualquier sección de PyG salvo solo-lectura.
  // W20.4: también en las tablas restringidas (Otros costes financieros 66x,
  // CAPEX-amortización 68x) — justo donde el cliente necesita crear líneas sin
  // saldo histórico; el prefijo se valida en cliente Y en servidor.
  const mostrarAnadirLinea = !readOnly && SECCIONES_PYG.has(seccionPyg);

  // Ejercicio destino = el más reciente (mayor año). La cuenta creada con saldo 0
  // en el último ejercicio aparece como línea nueva lista para proyectar.
  const ejercicioDestino = useMemo(() => {
    if (!ejercicios || ejercicios.length === 0) return null;
    return [...ejercicios].sort((a, b) => a.anio - b.anio)[ejercicios.length - 1];
  }, [ejercicios]);

  const handleCrearLinea = async () => {
    const codigo = nuevoCodigo.trim();
    const nombre = nuevoNombre.trim();
    if (!codigo || !nombre) {
      toast.error("Indica código y nombre de la cuenta");
      return;
    }
    if (!ejercicioDestino) {
      toast.error("No hay ejercicio destino para crear la línea");
      return;
    }
    // W20.4: en tablas acotadas (66x/68x…) el código debe empezar por el
    // prefijo de la pestaña. Se valida aquí y también en el servidor (R-6).
    if (prefijosFiltro && !prefijosFiltro.some((p) => codigo.startsWith(p))) {
      toast.error(`El código debe empezar por ${prefijosFiltro.join(" o ")}`);
      return;
    }
    setCreandoLinea(true);
    try {
      await crearCuenta(ejercicioDestino.id, {
        codigo_full: codigo,
        nombre,
        ...(prefijosFiltro ? { prefijos_permitidos: prefijosFiltro } : {}),
      });
      toast.success("Línea creada");
      setAddLineaOpen(false);
      setNuevoCodigo("");
      setNuevoNombre("");
      // Recargar la sección con el mismo mecanismo que tras guardar un driver.
      await loadLineas();
      baseCacheNivelRef.current = null;
      setBaseCache(null);
      if (onDriverSaved) await onDriverSaved();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Error al crear la línea");
    } finally {
      setCreandoLinea(false);
    }
  };

  // W19.4/5: borrar una cuenta manual (restaura el nombre PGC del grupo y
  // limpia en servidor los drivers que queden huérfanos) o renombrarla.
  const handleBorrarCuenta = async () => {
    if (!cuentaBorrando) return;
    setAccionandoCuenta(true);
    try {
      await borrarCuenta(cuentaBorrando.ejercicio_id, cuentaBorrando.cuenta_id);
      toast.success(`Cuenta ${cuentaBorrando.codigo} eliminada`);
      setCuentaBorrando(null);
      await loadLineas();
      baseCacheNivelRef.current = null;
      setBaseCache(null);
      if (onDriverSaved) await onDriverSaved();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Error al eliminar la cuenta");
    } finally {
      setAccionandoCuenta(false);
    }
  };

  const handleRenombrarCuenta = async () => {
    if (!cuentaRenombrando || !nombreNuevo.trim()) return;
    setAccionandoCuenta(true);
    try {
      if (cuentaRenombrando.empresa) {
        // A3 (sept-2026): renombrado por código en TODA la empresa (líneas a
        // Máx. dígitos de cualquier origen; sobrevive a reimportaciones).
        const res = await renombrarCuentaEmpresa(
          empresaId, cuentaRenombrando.codigo, nombreNuevo.trim()
        );
        const nCtas = res.data?.cuentas_actualizadas;
        toast.success(
          nCtas > 1
            ? `Cuenta renombrada en ${nCtas} ejercicios de la empresa`
            : "Cuenta renombrada"
        );
      } else {
        await renombrarCuenta(
          cuentaRenombrando.ejercicio_id, cuentaRenombrando.cuenta_id, nombreNuevo.trim()
        );
        toast.success("Cuenta renombrada");
      }
      setCuentaRenombrando(null);
      setNombreNuevo("");
      await loadLineas();
      baseCacheNivelRef.current = null;
      setBaseCache(null);
      if (onDriverSaved) await onDriverSaved();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Error al renombrar la cuenta");
    } finally {
      setAccionandoCuenta(false);
    }
  };

  // Punto 2 (Héctor oct-2026): alias de PRESENTACIÓN de una línea agregada
  // (1D/3D) — guarda proyeccion_config.alias_lineas[linea_id]; las cuentas
  // reales no cambian (en MAX sigue el renombrado real por código, A3).
  const handleGuardarAlias = async (nombre) => {
    if (!aliasEditando) return;
    const l = aliasEditando.linea;
    const lineaId = `${seccionPyg}_${l.nivel}_${l.codigo}`;
    setAccionandoCuenta(true);
    try {
      await saveProyeccionConfig(empresaId, { alias_lineas: { [lineaId]: nombre } });
      toast.success(nombre ? "Línea renombrada" : "Nombre original restaurado");
      setAliasEditando(null);
      await loadLineas();
      if (onDriverSaved) await onDriverSaved();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Error al renombrar la línea");
    } finally {
      setAccionandoCuenta(false);
    }
  };

  // R-10: líneas tras aplicar el filtro opcional por prefijo de código.
  const prefijosFiltro = useMemo(() => {
    if (!filtroCodigos) return null;
    return Array.isArray(filtroCodigos) ? filtroCodigos : [filtroCodigos];
  }, [filtroCodigos]);

  const lineasFiltradas = useMemo(() => {
    const base = !prefijosFiltro
      ? lineas
      : lineas.filter((l) => prefijosFiltro.some((p) => String(l.codigo).startsWith(p)));
    let conBloqueo = base;
    if (cuentasBloqueadas && cuentasBloqueadas.length > 0) {
      // El bloqueo del contenedor NO pisa un bloqueo real del motor. Se marca
      // `bloqueo_suave` para renderizarlo como la maqueta (fila gris, candado
      // y el motivo en texto plano, sin el badge "Bloqueada" del motor).
      // W20.2/W20.3: la proyección de una fila bloqueada se muestra a "—" (el
      // modelo NO la proyecta: p.ej. la 662 se informa desde el Pool bancario).
      conBloqueo = base.map((l) =>
        !l.bloqueada_por &&
        cuentasBloqueadas.some((p) => String(l.codigo).startsWith(p))
          ? { ...l, bloqueada_por: textoBloqueo, bloqueo_suave: true,
              proyeccion: null, es_fallback: false }
          : l
      );
    }
    if (!cuentasInformativas || cuentasInformativas.length === 0) return conBloqueo;
    const faltantes = cuentasInformativas
      .filter((c) => !conBloqueo.some((l) =>
        String(l.codigo).startsWith(c.codigo) || c.codigo.startsWith(String(l.codigo))))
      .map((c) => ({
        id: `informativa-${c.codigo}`,
        codigo: c.codigo,
        nombre: c.nombre,
        informativa: true,
        driver: null,
        valores_historicos: [],
        proyeccion: null,
        ratios: null,
        aviso: null,
        es_fallback: false,
        bloqueada_por: cuentasBloqueadas?.some((p) => c.codigo.startsWith(p))
          ? textoBloqueo
          : null,
        bloqueo_suave: cuentasBloqueadas?.some((p) => c.codigo.startsWith(p)) || false,
      }));
    return [...conBloqueo, ...faltantes].sort((x, y) =>
      String(x.codigo).localeCompare(String(y.codigo))
    );
  }, [lineas, prefijosFiltro, cuentasBloqueadas, textoBloqueo, cuentasInformativas]);

  // F2A: selección múltiple. Las líneas bloqueadas no se pueden seleccionar
  // (su driver depende de otra proyección y el backend las rechazaría).
  const permiteSeleccion = !readOnly;
  const lineasSeleccionables = useMemo(
    () => lineasFiltradas.filter((l) => !l.bloqueada_por && !l.informativa),
    [lineasFiltradas]
  );
  const toggleLinea = useCallback((id) => {
    setSeleccionadas((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);
  const todasSeleccionadas =
    lineasSeleccionables.length > 0 &&
    lineasSeleccionables.every((l) => seleccionadas.has(l.id));
  const algunaSeleccionada = lineasSeleccionables.some((l) => seleccionadas.has(l.id));
  const toggleTodas = useCallback(() => {
    setSeleccionadas((prev) => {
      const todas = lineasSeleccionables.every((l) => prev.has(l.id));
      if (todas) return new Set();
      return new Set(lineasSeleccionables.map((l) => l.id));
    });
  }, [lineasSeleccionables]);
  const abrirMasivo = () => {
    if (seleccionadas.size === 0) return;
    setMasivoOpen(true);
    loadBaseCache();
  };
  // Línea sintética estable para el diálogo masivo (evita reinicios del form si
  // el padre se re-renderiza mientras está abierto).
  const lineaMasivo = useMemo(
    () => ({ id: "__masivo__", codigo: "", nombre: "", valores_historicos: [], ratios: {} }),
    []
  );

  // ---- Mapas de ratios por línea ----
  const getValorHist = (linea, anio) =>
    linea.valores_historicos?.find((v) => v.anio === anio)?.valor ?? null;
  const getValorProy = (linea, anio) =>
    linea.proyeccion?.find((v) => v.anio === anio)?.valor ?? null;
  const getPct = (linea, anio) =>
    linea.ratios?.pct_sobre_ventas?.find((r) => r.anio === anio)?.pct ?? null;
  const getYoY = (linea, anio) =>
    linea.ratios?.yoy?.find((r) => r.anio === anio)?.pct ?? null;

  // ---- Fila TOTAL ----
  // W20.2: el TOTAL excluye las filas bloqueadas por el contenedor (p.ej. la
  // 662, que se informa en el Pool bancario) y las informativas: debe sumar
  // exactamente lo mismo que el modelo (tarjetas de resumen del backend).
  const totales = useMemo(() => {
    const sumables = lineasFiltradas.filter((l) => !l.bloqueo_suave && !l.informativa);
    const sum = {};
    const allYears = [...aniosHistoricos, ...aniosProyectados];
    allYears.forEach((anio) => {
      let total = null;
      sumables.forEach((l) => {
        const v = aniosHistoricos.includes(anio) ? getValorHist(l, anio) : getValorProy(l, anio);
        if (v !== null && v !== undefined) total = (total || 0) + v;
      });
      sum[anio] = total;
    });

    // Derivar ventas por año a partir de cualquier línea con pct conocido (valor / pct * 100)
    const ventas = {};
    allYears.forEach((anio) => {
      for (const l of lineasFiltradas) {
        const pct = getPct(l, anio);
        const v = aniosHistoricos.includes(anio) ? getValorHist(l, anio) : getValorProy(l, anio);
        if (pct !== null && pct !== undefined && pct !== 0 && v !== null && v !== undefined && v !== 0) {
          ventas[anio] = v / (pct / 100);
          break;
        }
      }
    });

    const pct = {};
    const yoy = {};
    allYears.forEach((anio, idx) => {
      pct[anio] = sum[anio] !== null && ventas[anio] ? (sum[anio] / ventas[anio]) * 100 : null;
      if (idx > 0) {
        const prev = sum[allYears[idx - 1]];
        yoy[anio] = sum[anio] !== null && prev !== null && prev !== undefined && prev !== 0
          ? ((sum[anio] - prev) / Math.abs(prev)) * 100
          : null;
      } else {
        yoy[anio] = null;
      }
    });

    return { sum, pct, yoy };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineasFiltradas, aniosHistoricos, aniosProyectados]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
      </div>
    );
  }

  const nCols =
    (permiteSeleccion ? 1 : 0) + 2 + aniosHistoricos.length + aniosProyectados.length + 2;

  return (
    <div className="space-y-6">
      {/* Header con selectores y leyenda */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        {/* Sin titulo/descripcion el header solo muestra los selectores: los
            contenedores con tarjetas de subapartado (Financiación/CAPEX) no
            repiten el título debajo (petición del cliente, 2026-08-19). */}
        {titulo || descripcion ? (
          <div>
            {titulo ? <h2 className="section-title">{titulo}</h2> : null}
            {descripcion ? <p className="text-sm text-muted-foreground">{descripcion}</p> : null}
          </div>
        ) : <div />}
        <div className="flex items-center gap-4">
          {/* Con nivel controlado, el selector vive UNA sola vez en la
              cabecera de la página (global y destacado); aquí no se repite.
              Destacado en ámbar como el "Año base" (Raúl 30-09: "que se vea"). */}
          {!nivelControlado && (
          <div
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg border-2 border-amber-400 bg-amber-50 dark:bg-amber-950/30"
            data-testid="nivel-selector"
          >
            <Label className="text-sm font-semibold text-amber-700 dark:text-amber-400 whitespace-nowrap">Nivel:</Label>
            <Select value={nivel} onValueChange={setNivel}>
              <SelectTrigger className="w-[150px] h-8 text-sm font-medium border-amber-400/60 bg-background">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {nivelesProyeccion
                  .filter((n) => !nivelesEfectivos || nivelesEfectivos.includes(n.id))
                  .map((n) => (
                    <SelectItem key={n.id} value={n.id}>
                      {n.label}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
          )}
          {mostrarAnadirLinea && (
            <Button
              size="sm"
              variant="outline"
              className="gap-1.5"
              onClick={() => setAddLineaOpen(true)}
              disabled={!ejercicioDestino}
              title={
                ejercicioDestino
                  ? undefined
                  : "No hay ejercicios cargados para crear la línea"
              }
              data-testid="btn-anadir-linea"
            >
              <Plus className="h-4 w-4" />
              Añadir línea
            </Button>
          )}
        </div>
      </div>

      {/* Leyenda de badges. W20.11: solo los tipos de driver DISPONIBLES en
          esta tabla (con tiposDriverPermitidos restringido, p.ej. la
          amortización a "Valor fijo", anunciar los 6 tipos engañaba). */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-muted-foreground">
        <div className="flex items-center gap-1.5">
          <Info className="h-3.5 w-3.5" />
          <span>Drivers:</span>
        </div>
        {[
          { id: "crecimiento", icon: TrendingUp, cls: "text-primary", label: "Crecimiento %" },
          { id: "valor_fijo", icon: Lock, cls: "text-indigo-600 dark:text-indigo-400", label: "Valor fijo" },
          { id: "porcentaje_de", icon: Link2, cls: "text-sky-600 dark:text-sky-400", label: "% de otra línea" },
          { id: "elasticidad", icon: Gauge, cls: "text-violet-600 dark:text-violet-400", label: "Elasticidad" },
          { id: "pxq", icon: Package, cls: "text-emerald-600 dark:text-emerald-400", label: "Volumen × Precio" },
        ]
          .filter((t) => !tiposDriverPermitidos || tiposDriverPermitidos.includes(t.id))
          .map(({ id, icon: IconoTipo, cls, label }) => (
            <span key={id} className="inline-flex items-center gap-1">
              <IconoTipo className={cn("h-3 w-3", cls)} /> {label}
            </span>
          ))}
        <span className="inline-flex items-center gap-1">
          <AlertTriangle className="h-3 w-3 text-primary" /> Bloqueada por otra proyección
        </span>
        {readOnly && (
          <Badge variant="outline" className="text-[11px]">
            Solo lectura
          </Badge>
        )}
      </div>

      {/* F2A: barra de acciones para la selección múltiple */}
      {permiteSeleccion && seleccionadas.size > 0 && (
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-lg border border-primary/30 bg-primary/5 px-4 py-2.5">
          <span className="text-sm font-medium">
            {seleccionadas.size} línea{seleccionadas.size === 1 ? "" : "s"} seleccionada{seleccionadas.size === 1 ? "" : "s"}
          </span>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => setSeleccionadas(new Set())}>
              Limpiar
            </Button>
            <Button size="sm" className="gap-2" onClick={abrirMasivo}>
              <TrendingUp className="h-4 w-4" />
              Aplicar driver a {seleccionadas.size} seleccionada{seleccionadas.size === 1 ? "" : "s"}
            </Button>
          </div>
        </div>
      )}

      {/* Tabla */}
      <Card>
        <CardContent className="p-0 overflow-x-auto">
          {/* Rejilla fija (tabla-anios): dos TablaDrivers en la misma pantalla
              (p. ej. Financiación → Otros, grupos 66 y 76) quedan alineadas */}
          <Table className={TABLA_ANIOS}>
            <ColsAnios
              n={aniosHistoricos.length + aniosProyectados.length}
              antes={permiteSeleccion ? [40, 90] : [90]}
              despues={[110, 150]}
            />
            <TableHeader>
              {/* Fila de grupos Histórico / Proyección */}
              <TableRow className="border-b-0 hover:bg-transparent">
                {permiteSeleccion && <TableHead className="h-7 w-[40px]" />}
                <TableHead colSpan={2} className="h-7" />
                {aniosHistoricos.length > 0 && (
                  <TableHead
                    colSpan={aniosHistoricos.length}
                    className="h-7 text-center text-[11px] font-semibold uppercase tracking-widest text-muted-foreground bg-muted/30"
                  >
                    Histórico
                  </TableHead>
                )}
                <TableHead
                  colSpan={aniosProyectados.length}
                  className="h-7 text-center text-[11px] font-semibold uppercase tracking-widest text-primary bg-primary/5 border-l-2 border-primary/40"
                >
                  Proyección
                </TableHead>
                <TableHead colSpan={2} className="h-7" />
              </TableRow>
              {/* Fila de años */}
              <TableRow className="bg-muted/30">
                {permiteSeleccion && (
                  <TableHead className="w-[40px] text-center">
                    <Checkbox
                      checked={
                        todasSeleccionadas ? true : algunaSeleccionada ? "indeterminate" : false
                      }
                      onCheckedChange={toggleTodas}
                      disabled={lineasSeleccionables.length === 0}
                      aria-label="Seleccionar todas las líneas visibles"
                    />
                  </TableHead>
                )}
                <TableHead className="w-[90px]">Código</TableHead>
                <TableHead>Línea</TableHead>
                {aniosHistoricos.map((anio) => (
                  <TableHead key={anio} className="text-right bg-muted/30">
                    {anio}
                  </TableHead>
                ))}
                {aniosProyectados.map((anio, i) => (
                  <TableHead
                    key={anio}
                    className={cn(
                      "text-right w-[120px] bg-primary/5 text-primary font-semibold",
                      i === 0 && "border-l-2 border-primary/40"
                    )}
                  >
                    {anio}
                  </TableHead>
                ))}
                <TableHead className="text-center w-[110px]">Tendencia</TableHead>
                <TableHead className="text-center w-[150px]">Driver</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {lineasFiltradas.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={nCols} className="text-center py-8 text-muted-foreground">
                    <AlertCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p>No hay líneas disponibles para esta sección</p>
                    <p className="text-xs mt-1">Importa datos de ejercicios para ver las líneas</p>
                  </TableCell>
                </TableRow>
              ) : (
                <>
                  {lineasFiltradas.map((linea) => {
                    const histValores = aniosHistoricos.map((a) => getValorHist(linea, a));
                    const proyValores = aniosProyectados.map((a) => getValorProy(linea, a));
                    const expandible = nivel !== "MAX" && !linea.informativa && (linea.n_cuentas || 0) > 0;
                    const subFilas = expandidas.has(linea.codigo)
                      ? (lineasMax || []).filter(
                          (s) => String(s.codigo).startsWith(String(linea.codigo)) && s.id !== linea.id
                        )
                      : [];
                    return (
                      <Fragment key={linea.id}>
                      <TableRow
                        className={cn(
                          "hover:bg-muted/20",
                          permiteSeleccion && seleccionadas.has(linea.id) && "bg-primary/5",
                          linea.bloqueo_suave && "opacity-60 bg-muted/10"
                        )}
                      >
                        {permiteSeleccion && (
                          <TableCell className="text-center">
                            {linea.bloqueo_suave ? (
                              <Lock
                                className="h-3.5 w-3.5 mx-auto text-muted-foreground"
                                aria-label={`Línea ${linea.codigo} bloqueada: ${linea.bloqueada_por}`}
                              />
                            ) : (
                              <Checkbox
                                checked={seleccionadas.has(linea.id)}
                                onCheckedChange={() => toggleLinea(linea.id)}
                                disabled={!!linea.bloqueada_por || !!linea.informativa}
                                aria-label={`Seleccionar línea ${linea.codigo}`}
                              />
                            )}
                          </TableCell>
                        )}
                        <TableCell className="font-mono text-sm">
                          <span className="inline-flex items-center gap-1">
                            {/* UX drivers (sept-2026): desplegar las subcuentas
                                de la línea SIN cambiar el zoom global — mirar
                                con el selector, trabajar fino desplegando. */}
                            {expandible && (
                              <button
                                type="button"
                                onClick={() => toggleExpand(linea.codigo)}
                                title="Ver y afinar las subcuentas de esta línea"
                                data-testid={`expandir-${linea.codigo}`}
                                className="shrink-0 text-muted-foreground hover:text-foreground"
                              >
                                {expandidas.has(linea.codigo)
                                  ? <ChevronDown className="h-3.5 w-3.5" />
                                  : <ChevronRight className="h-3.5 w-3.5" />}
                              </button>
                            )}
                            {linea.codigo}
                          </span>
                        </TableCell>
                        <TableCell className="font-medium">
                          <span className="inline-flex items-center gap-1">
                            {linea.nombre}
                            {/* B2 (sept-2026): en niveles agregados el nombre
                                es el de la PRIMERA subcuenta pero el importe
                                suma TODAS — se señala para no confundir
                                (caso real: 705 de EDSER = Prestaciones +
                                Transporte). */}
                            {linea.n_cuentas > 1 && (
                              <span
                                className="shrink-0 rounded bg-muted px-1 py-0.5 text-[10px] font-mono text-muted-foreground cursor-help"
                                title={`El importe de esta línea es la SUMA de ${linea.n_cuentas} subcuentas (el nombre es el de la primera). Cambia el nivel a "Máx. dígitos" para verlas por separado.`}
                              >
                                Σ {linea.n_cuentas}
                              </span>
                            )}
                            {/* A3 (Héctor 29-09): renombrar cualquier cuenta
                                desde la propia tabla (a Máx. dígitos, donde
                                línea = cuenta). Propaga a toda la empresa. */}
                            {!readOnly && linea.nivel === "MAX" &&
                              !linea.informativa &&
                              !esCuentaDeAsiento(linea.codigo) &&
                              (!linea.cuentas_manuales || linea.cuentas_manuales.length === 0) && (
                              <button
                                type="button"
                                className="shrink-0 opacity-40 hover:opacity-100 transition-opacity"
                                title="Renombrar la cuenta (en toda la empresa)"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setCuentaRenombrando({ codigo: linea.codigo, empresa: true });
                                  setNombreNuevo(linea.nombre || "");
                                }}
                                data-testid={`renombrar-linea-${linea.codigo}`}
                              >
                                <Pencil className="h-3 w-3" />
                              </button>
                            )}
                            {/* Punto 2 (Héctor oct-2026): renombrar también
                                las líneas AGREGADAS (1D/3D) — alias de
                                presentación; las cuentas reales no cambian. */}
                            {!readOnly && linea.nivel !== "MAX" && !linea.informativa && (
                              <button
                                type="button"
                                className="shrink-0 opacity-40 hover:opacity-100 transition-opacity"
                                title="Renombrar esta línea (solo el nombre que se muestra; las cuentas no cambian)"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setAliasEditando({ linea, nombre: linea.nombre || "" });
                                }}
                                data-testid={`alias-linea-${linea.codigo}`}
                              >
                                <Pencil className="h-3 w-3" />
                              </button>
                            )}
                            {linea.aviso && (
                              <span
                                title={`${linea.aviso.mensaje} (${linea.aviso.variacion_pct > 0 ? "+" : ""}${linea.aviso.variacion_pct}%)`}
                                className="shrink-0"
                              >
                                <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
                              </span>
                            )}
                            {/* W19.4/5: la línea cubre cuentas de alta manual →
                                se pueden renombrar (restaurar el nombre) o
                                borrar (el grupo recupera el nombre PGC). */}
                            {!readOnly && (linea.cuentas_manuales || []).length > 0 && (
                              <span className="inline-flex items-center gap-0.5 shrink-0">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-6 w-6 text-muted-foreground hover:text-foreground"
                                  title={`Renombrar la cuenta manual ${linea.cuentas_manuales[0].codigo}`}
                                  onClick={() => {
                                    setCuentaRenombrando(linea.cuentas_manuales[0]);
                                    setNombreNuevo(linea.cuentas_manuales[0].nombre || "");
                                  }}
                                  data-testid={`renombrar-cuenta-${linea.codigo}`}
                                >
                                  <Pencil className="h-3 w-3" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-6 w-6 text-muted-foreground hover:text-destructive"
                                  title={`Eliminar la cuenta manual ${linea.cuentas_manuales[0].codigo}`}
                                  onClick={() => setCuentaBorrando(linea.cuentas_manuales[0])}
                                  data-testid={`borrar-cuenta-${linea.codigo}`}
                                >
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              </span>
                            )}
                          </span>
                        </TableCell>
                        {aniosHistoricos.map((anio) => (
                          <TableCell key={anio} className="align-top py-2">
                            <CeldaAnio
                              valor={getValorHist(linea, anio)}
                              pct={getPct(linea, anio)}
                              esProyectada={false}
                            />
                          </TableCell>
                        ))}
                        {aniosProyectados.map((anio, i) => (
                          <TableCell
                            key={anio}
                            className={cn(
                              "align-top py-2 bg-primary/5",
                              i === 0 && "border-l-2 border-primary/40"
                            )}
                          >
                            <CeldaAnio
                              valor={getValorProy(linea, anio)}
                              pct={getPct(linea, anio)}
                              yoy={getYoY(linea, anio)}
                              esProyectada
                              invertirSemantica={invertirSemantica}
                              destacarYoY={i === 0}
                              esFallback={!!linea.es_fallback}
                            />
                          </TableCell>
                        ))}
                        <TableCell className="text-center">
                          <Sparkline historico={histValores} proyeccion={proyValores} />
                        </TableCell>
                        <TableCell className="text-center">
                          <DriverBadge
                            linea={linea}
                            readOnly={readOnly}
                            onClick={linea.bloqueada_por || linea.informativa ? undefined : () => handleConfigClick(linea)}
                          />
                          {/* W19.2: driver guardado pero fuera del nivel de
                              proyección efectivo (Drivers → Generales). */}
                          {linea.driver && linea.driver_ignorado && (
                            <div className="text-[11px] text-amber-600 dark:text-amber-400 mt-0.5">
                              No aplica (nivel de proyección)
                            </div>
                          )}
                          {/* UX drivers (sept-2026): driver propio con
                              subcuentas que van por libre — se declara y se
                              ofrece resolver con un clic. Los importes de la
                              fila ya son el agregado EFECTIVO del motor. */}
                          {linea.driver && linea.driver_parcial && (
                            <div className="mt-0.5 space-y-0.5">
                              <div
                                className="text-[11px] text-amber-600 dark:text-amber-400 cursor-help"
                                title={`Van por su propio driver: ${linea.driver_parcial.drivers.map((d) => `${d.codigo} (${d.descripcion})`).join(" · ")}. Los importes de esta fila son el agregado efectivo real.`}
                              >
                                ⚠ {linea.driver_parcial.n_cuentas_fuera} subcuenta{linea.driver_parcial.n_cuentas_fuera > 1 ? "s" : ""} por su propio driver
                              </div>
                              {!readOnly && (
                                <div className="flex flex-col items-center gap-0.5 text-[10px]">
                                  <button
                                    type="button"
                                    className="text-primary hover:underline"
                                    data-testid={`resolver-subs-${linea.codigo}`}
                                    onClick={() =>
                                      setResolverPendiente({
                                        ids: linea.driver_parcial.drivers.map(
                                          (d) => `${seccionPyg}_${d.nivel}_${d.codigo}`
                                        ),
                                        texto: `Se anularán ${linea.driver_parcial.n_drivers} driver${linea.driver_parcial.n_drivers > 1 ? "s" : ""} de subcuenta (${linea.driver_parcial.drivers.map((d) => d.codigo).join(", ")}) y la línea ${linea.codigo} volverá a gobernar TODAS sus cuentas con su driver actual.`,
                                      })
                                    }
                                  >
                                    Resolver: que {linea.codigo} gobierne todo
                                  </button>
                                  <button
                                    type="button"
                                    className="text-primary hover:underline"
                                    data-testid={`resolver-propio-${linea.codigo}`}
                                    onClick={() =>
                                      setResolverPendiente({
                                        ids: [linea.id],
                                        texto: `Se anulará el driver de la ${linea.codigo}. Sus subcuentas con driver propio seguirán con él y el resto pasará al automático (o al driver de nivel superior si lo hay).`,
                                      })
                                    }
                                  >
                                    Resolver: todo por subcuentas
                                  </button>
                                </div>
                              )}
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                      {/* Subcuentas desplegadas inline (solo lectura de
                          valores + Configurar): trabajar fino sin cambiar el
                          zoom global. */}
                      {expandidas.has(linea.codigo) && cargandoSub && !lineasMax && (
                        <TableRow className="bg-muted/10">
                          <TableCell colSpan={colSpanTotal} className="text-center text-xs text-muted-foreground py-2">
                            <Loader2 className="h-4 w-4 animate-spin inline mr-2" />
                            Cargando subcuentas…
                          </TableCell>
                        </TableRow>
                      )}
                      {subFilas.map((sub) => (
                        <TableRow key={sub.id} className="bg-muted/10 hover:bg-muted/20">
                          {permiteSeleccion && <TableCell />}
                          <TableCell className="font-mono text-xs pl-8 text-muted-foreground whitespace-nowrap">
                            ↳ {sub.codigo}
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">
                            {sub.nombre}
                          </TableCell>
                          {aniosHistoricos.map((anio) => (
                            <TableCell key={anio} className="align-top py-1">
                              <CeldaAnio valor={getValorHist(sub, anio)} pct={null} esProyectada={false} />
                            </TableCell>
                          ))}
                          {aniosProyectados.map((anio, i) => (
                            <TableCell key={anio}
                              className={cn("align-top py-1 bg-primary/5", i === 0 && "border-l-2 border-primary/40")}>
                              <CeldaAnio
                                valor={getValorProy(sub, anio)} pct={null} esProyectada
                                invertirSemantica={invertirSemantica}
                                esFallback={!!sub.es_fallback}
                              />
                            </TableCell>
                          ))}
                          <TableCell className="text-center text-xs text-muted-foreground">—</TableCell>
                          <TableCell className="text-center">
                            <DriverBadge
                              linea={sub}
                              readOnly={readOnly}
                              onClick={sub.bloqueada_por || sub.informativa ? undefined : () => handleConfigClick(sub)}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                      </Fragment>
                    );
                  })}
                  {/* Fila TOTAL */}
                  <TableRow className="border-t-2 bg-muted/40 hover:bg-muted/40 font-semibold">
                    {permiteSeleccion && <TableCell />}
                    <TableCell className="font-mono text-sm">Σ</TableCell>
                    <TableCell>TOTAL</TableCell>
                    {aniosHistoricos.map((anio) => (
                      <TableCell key={anio} className="align-top py-2">
                        <CeldaAnio
                          valor={totales.sum[anio]}
                          pct={totales.pct[anio]}
                          esProyectada={false}
                        />
                      </TableCell>
                    ))}
                    {aniosProyectados.map((anio, i) => (
                      <TableCell
                        key={anio}
                        className={cn(
                          "align-top py-2 bg-primary/10",
                          i === 0 && "border-l-2 border-primary/40"
                        )}
                      >
                        <CeldaAnio
                          valor={totales.sum[anio]}
                          pct={totales.pct[anio]}
                          yoy={totales.yoy[anio]}
                          esProyectada
                          invertirSemantica={invertirSemantica}
                          destacarYoY={i === 0}
                        />
                      </TableCell>
                    ))}
                    <TableCell />
                    <TableCell />
                  </TableRow>
                </>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Dialog de configuración */}
      <LineConfigDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        linea={selectedLinea}
        aniosProyectados={aniosProyectados}
        onSave={handleSaveLinea}
        baseCache={baseCache}
        baseCacheLoading={baseCacheLoading}
        nivel={nivel}
        readOnly={readOnly}
        tiposPermitidos={tiposDriverPermitidos}
        ocultarValorFijoIpc={ocultarValorFijoIpc}
        todosDrivers={todosDrivers}
        onAnularDrivers={anularDrivers}
      />

      {/* F2A: mismo diálogo en modo masivo. `linea` es un objeto sintético (sin
          histórico) para reutilizar buildDriver/formularios sin tocarlos. */}
      <LineConfigDialog
        open={masivoOpen}
        onOpenChange={setMasivoOpen}
        linea={lineaMasivo}
        aniosProyectados={aniosProyectados}
        onSave={handleSaveMasivo}
        baseCache={baseCache}
        baseCacheLoading={baseCacheLoading}
        nivel={nivel}
        tiposPermitidos={tiposDriverPermitidos}
        ocultarValorFijoIpc={ocultarValorFijoIpc}
        masivo
        masivoCount={seleccionadas.size}
      />

      {/* Añadir línea: crea una cuenta con saldo 0 en el ejercicio más reciente.
          El motor la clasifica por su código y aparece como línea proyectable. */}
      <Dialog open={addLineaOpen} onOpenChange={setAddLineaOpen}>
        <DialogContent className="sm:max-w-[440px]">
          <DialogHeader>
            <DialogTitle>Añadir línea de {seccionesLabel[seccionPyg]?.toLowerCase() || "PyG"}</DialogTitle>
            <DialogDescription>
              Crea una cuenta nueva (saldo 0) para proyectar una línea que no
              existía en la contabilidad
              {ejercicioDestino ? ` del ejercicio ${ejercicioDestino.anio}` : ""}.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="nueva-linea-codigo">Código de cuenta</Label>
              <Input
                id="nueva-linea-codigo"
                value={nuevoCodigo}
                onChange={(e) => setNuevoCodigo(e.target.value)}
                placeholder="Ej: 705000"
                disabled={creandoLinea}
                data-testid="input-nueva-linea-codigo"
              />
              <p className="text-xs text-muted-foreground">
                {prefijosFiltro
                  ? `Debe empezar por ${prefijosFiltro.join(" o ")} para aparecer en esta pestaña.`
                  : `Debe empezar por el prefijo de la sección (${hintPrefijoSeccion[seccionPyg]}) para aparecer aquí.`}{" "}
                Un código corto se completa con ceros a la longitud estándar
                (705 → 70500000).
              </p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="nueva-linea-nombre">Nombre</Label>
              <Input
                id="nueva-linea-nombre"
                value={nuevoNombre}
                onChange={(e) => setNuevoNombre(e.target.value)}
                placeholder="Ej: Ventas nueva línea de negocio"
                disabled={creandoLinea}
                data-testid="input-nueva-linea-nombre"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddLineaOpen(false)} disabled={creandoLinea}>
              Cancelar
            </Button>
            <Button
              onClick={handleCrearLinea}
              disabled={creandoLinea || !nuevoCodigo.trim() || !nuevoNombre.trim()}
              className="gap-2"
              data-testid="btn-crear-linea"
            >
              <Plus className="h-4 w-4" />
              Crear línea
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* W19.4/5: confirmación de borrado de una cuenta manual */}
      <AlertDialog open={!!cuentaBorrando} onOpenChange={(o) => !o && setCuentaBorrando(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar la cuenta manual?</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará la cuenta {cuentaBorrando?.codigo} «{cuentaBorrando?.nombre}»
              creada desde «Añadir línea». La línea recuperará su nombre estándar del PGC
              y los drivers que queden sin cuentas también se limpiarán.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={accionandoCuenta}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleBorrarCuenta();
              }}
              disabled={accionandoCuenta}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirmar-borrar-cuenta"
            >
              {accionandoCuenta && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
              Eliminar cuenta
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* UX drivers (sept-2026): confirmación del "Resolver" de solapes */}
      <AlertDialog open={!!resolverPendiente} onOpenChange={(o) => !o && setResolverPendiente(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Resolver el solape de drivers?</AlertDialogTitle>
            <AlertDialogDescription>{resolverPendiente?.texto}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={accionandoCuenta}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                ejecutarResolver();
              }}
              disabled={accionandoCuenta}
              data-testid="confirmar-resolver"
            >
              {accionandoCuenta ? "Resolviendo…" : "Sí, resolver"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* W19.4: renombrar una cuenta manual */}
      <Dialog
        open={!!cuentaRenombrando}
        onOpenChange={(o) => {
          if (!o) {
            setCuentaRenombrando(null);
            setNombreNuevo("");
          }
        }}
      >
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle>Renombrar cuenta {cuentaRenombrando?.codigo}</DialogTitle>
            <DialogDescription>
              {cuentaRenombrando?.empresa
                ? "El nombre nuevo se aplica en TODOS los ejercicios de la empresa y se mantiene aunque vuelvas a importar."
                : "Cambia el nombre de esta cuenta creada manualmente."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5 py-2">
            <Label htmlFor="renombrar-cuenta-nombre">Nombre</Label>
            <Input
              id="renombrar-cuenta-nombre"
              value={nombreNuevo}
              onChange={(e) => setNombreNuevo(e.target.value)}
              disabled={accionandoCuenta}
              data-testid="input-renombrar-cuenta"
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCuentaRenombrando(null)}
              disabled={accionandoCuenta}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleRenombrarCuenta}
              disabled={accionandoCuenta || !nombreNuevo.trim()}
              data-testid="btn-renombrar-cuenta"
            >
              {accionandoCuenta && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
              Guardar nombre
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Punto 2 (Héctor oct-2026): renombrar una línea AGREGADA (1D/3D) —
          alias de presentación, las cuentas reales quedan intactas. */}
      <Dialog open={!!aliasEditando} onOpenChange={(o) => !o && setAliasEditando(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Renombrar línea {aliasEditando?.linea?.codigo}</DialogTitle>
            <DialogDescription>
              Cambia el nombre que se MUESTRA para esta línea agregada
              {aliasEditando?.linea?.n_cuentas > 1
                ? ` (suma de ${aliasEditando.linea.n_cuentas} subcuentas)`
                : ""}
              . Las cuentas reales no cambian; a Máx. dígitos seguirás viendo
              cada una con su nombre.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5 py-2">
            <Label htmlFor="alias-linea-nombre">Nombre</Label>
            <Input
              id="alias-linea-nombre"
              value={aliasEditando?.nombre ?? ""}
              onChange={(e) => setAliasEditando((s) => ({ ...s, nombre: e.target.value }))}
              disabled={accionandoCuenta}
              data-testid="input-alias-linea"
            />
            {aliasEditando?.linea?.nombre_original && (
              <p className="text-[11px] text-muted-foreground">
                Nombre original: {aliasEditando.linea.nombre_original}
              </p>
            )}
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            {aliasEditando?.linea?.nombre_original && (
              <Button
                variant="outline"
                className="sm:mr-auto"
                onClick={() => handleGuardarAlias(null)}
                disabled={accionandoCuenta}
                data-testid="btn-alias-restaurar"
              >
                Restaurar original
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => setAliasEditando(null)}
              disabled={accionandoCuenta}
            >
              Cancelar
            </Button>
            <Button
              onClick={() => handleGuardarAlias(aliasEditando?.nombre?.trim() || null)}
              disabled={accionandoCuenta || !aliasEditando?.nombre?.trim()}
              data-testid="btn-alias-guardar"
            >
              {accionandoCuenta && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
              Guardar nombre
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
